/**
 * Order amends + line cancellation/retention flow (loaded before tracker-v2.js).
 * Exposes window.OrderAmendsFlow for tracker-v2 lifecycle hooks.
 */

(function (global) {
    /** Canonical cancellation reason IDs (API / removalReason / cancellationReason). */
    const CANCELLATION_REASON = {
        AVAILABILITY: 'availability',
        LJ_INSTEAD: 'lj_instead',
        ELSEWHERE: 'elsewhere',
        CHANGED_MIND: 'changed_mind',
        PRICE: 'price',
        CHEAPER_BETTER: 'cheaper_better',
        SHIPPING_REFUND_DUE_TO_DELAY: 'shipping_refund_due_to_delay',
    };

    const REASON_BRANCH = {
        [CANCELLATION_REASON.PRICE]: 'price',
        [CANCELLATION_REASON.AVAILABILITY]: 'deliveryDate',
    };

    const NO_RETENTION_BY_CONTEXT = {
        line: new Set([
            CANCELLATION_REASON.CHEAPER_BETTER,
            CANCELLATION_REASON.ELSEWHERE,
            CANCELLATION_REASON.CHANGED_MIND,
        ]),
        order: new Set([
            CANCELLATION_REASON.LJ_INSTEAD,
            CANCELLATION_REASON.ELSEWHERE,
            CANCELLATION_REASON.CHANGED_MIND,
        ]),
    };

    function isNoRetentionReason(scope, reasonId) {
        return NO_RETENTION_BY_CONTEXT[scope]?.has(reasonId) ?? false;
    }

    /** Numeric IDs for Order Amend API POST (`cancellationReasonId`). */
    const ORDER_CANCEL_REASON_ID = {
        [CANCELLATION_REASON.AVAILABILITY]: 8,
        [CANCELLATION_REASON.LJ_INSTEAD]: 4,
        [CANCELLATION_REASON.CHEAPER_BETTER]: 4,
        [CANCELLATION_REASON.ELSEWHERE]: 1,
        [CANCELLATION_REASON.CHANGED_MIND]: 6,
        [CANCELLATION_REASON.PRICE]: 5,
    };

    /** `order-details-update` POST when customer accepts shipping refund on order cancel. */
    const ORDER_SHIPPING_REFUND_REASON_ID = 12;

    const STORE_CONFIG_BY_DOMAIN = {
        'laura-james-furniture.myshopify.com': { currencyId: 1, priceLevelId: 2, locationId: 17 },  // Laura James UK
        'laura-james-ireland.myshopify.com': { currencyId: 4, priceLevelId: 4, locationId: 17 },     // Laura James Ireland
        'rattandirect.myshopify.com': { currencyId: 1, priceLevelId: 16, locationId: 40 },           // Rattan Direct
        'laura-james-france.myshopify.com': { currencyId: 4, priceLevelId: 3, locationId: 17 },      // Laura James France
        'zjpntr-5b.myshopify.com': { currencyId: 1, priceLevelId: 13, locationId: 51 },              // Rattan Direct B2B
        'laura-james-germany.myshopify.com': { currencyId: 1, priceLevelId: 13, locationId: 41 },    // Laura James B2B
        'vulcan-foundry.myshopify.com': { currencyId: 1, priceLevelId: 17, locationId: 44 },         // Vulcan Foundry
    };

    const DEFAULT_STORE_CONFIG = STORE_CONFIG_BY_DOMAIN['laura-james-furniture.myshopify.com'];

    function getStoreConfig() {
        const domain = global.Shopify?.shop;
        const config = domain ? STORE_CONFIG_BY_DOMAIN[domain] : null;
        if (!config) {
            console.warn('[Order amends] Unrecognised Shopify.shop domain, falling back to Laura James UK store config:', domain);
            return DEFAULT_STORE_CONFIG;
        }
        return config;
    }

    function themeReasonToCancellationReasonId(themeReasonId) {
        return ORDER_CANCEL_REASON_ID[themeReasonId];
    }

    function toApiLineId(raw) {
        const s = String(raw ?? '').trim();
        if (!s) return '';
        return s.slice(s.lastIndexOf('-') + 1);
    }

    function buildOrderCancelPayload(themeReasonId, fullCancelOrder = false, extras = {}) {
        const payload = {
            action: 'cancelOrderAndRefund',
            platformOrderNum: String(getOrderContext()?.orderSummary?.orderNo ?? ''),
            refundTotal: 0,
            currencySymbol: getOrderContext()?.orderSummary?.currencySymbol ?? 'GBP',
            refunds: getOrderContext()?.orderSummary?.refundTotals ?? [],
            total: getOrderContext()?.orderSummary?.transactionTotal ?? 0,
            fullCancelOrder: fullCancelOrder,
        };
        if (themeReasonId) {
            const cancellationReasonId = themeReasonToCancellationReasonId(themeReasonId);
            if (cancellationReasonId != null) {
                payload.cancellationReasonId = cancellationReasonId;
            }
        }
        if (extras.items?.length) {
            payload.items = extras.items.map((item) => {
                const row = {
                    lineId: toApiLineId(item.lineId),
                    refundedQty: item.refundedQty,
                };
                if (item.grossAmount != null) row.grossAmount = item.grossAmount;
                if (item.cancellationReasonId != null) {
                    row.cancellationReasonId = item.cancellationReasonId;
                }
                return row;
            });
        }
        if (extras.refundTotal != null) payload.refundTotal = extras.refundTotal;
        if (extras.currencySymbol) payload.currencySymbol = extras.currencySymbol;
        return payload;
    }

    function roundMoneyAmount(value) {
        return Math.round(parseNumber(value) * 100) / 100;
    }

    function computeOrderCancelRefundSummary() {
        const orderData = getOrderContext();
        const items = [];
        const productLines = [];
        let itemsGrossTotal = 0;
        const discountTotal = parseNumber(orderData?.orderSummary?.discountTotal);

        orderAmendsItemRegistry.forEach((entry) => {
            const item = entry.item || {};
            const qty = Math.max(0, parseInt(item.quantity, 10) || 0);
            const gross = parseNumber(item.grossAmount);
            const lineId = String(item.id ?? item.itemNumber ?? '');
            const description = String(item.itemDescription ?? '').trim();

            itemsGrossTotal += gross;
            items.push({
                lineId,
                refundedQty: qty,
                grossAmount: gross,
            });
            productLines.push({
                description,
                quantity: qty,
                grossAmount: gross,
                displayLine: `${description} x${qty} ${formatMoney(gross)}`,
            });
        });

        const shippingTotal = roundMoneyAmount(orderData?.orderSummary?.shippingTotal);
        const refundTotal = (itemsGrossTotal + shippingTotal) - discountTotal;

        return {
            items,
            itemsGrossTotal: roundMoneyAmount(itemsGrossTotal),
            shippingTotal,
            refundTotal,
            productLines,
            discountTotal,
        };
    }

    function buildOrderCancelProductListHtml(productLines) {
        if (!productLines?.length) return '';
        const listItems = productLines
            .map((line) => `<li>${escapeHtml(line.displayLine)}</li>`)
            .join('');
        return `<ul class="order-cancel-success__product-list">${listItems}</ul>`;
    }

    function buildOrderCancelSuccessMessageHtml(variant, { amount, productListHtml = '' }) {
        const tr = t();
        const amountStr = formatMoney(amount);
        let template;

        if (variant === 'shipping') {
            template =
                tr.orderAmendsCancellationShippingSuccessMessageHtml ||
                'The shipping fee of <strong>{{ amount }}</strong> will be refunded to your original payment method. Refunds are typically processed within 3–5 business days, depending on your bank or payment provider.';
        } else {
            template =
                tr.orderAmendsCancelOrderFullSuccessHtml ||
                '<p><strong>Your cancellation request was submitted successfully!</strong></p><p>The total amount of <strong>{{ amount }}</strong> will be refunded to your original payment method. Refunds are typically processed within 3–5 business days, depending on your bank and payment provider.</p>';
        }

        return String(template)
            .replace(/\{\{\s*amount\s*\}\}/g, amountStr)
            .replace(/\{\{\s*product_list\s*\}\}/g, productListHtml);
    }

    let orderAmendsEditModeActive = false;
    let orderCancelFlowActive = false;
    let orderCancelDrawerState = {
        step: 'reason',
        selectedReason: null,
        pendingReason: null,
        pendingShippingRetentionChoice: null,
        reasonScreenVariant: 'B',
        preCheckResults: null,
        activeRetentionPath: null,
        cancellationResult: null,
        deliveryDateRetentionChecks: null,
    };
    let transactionSummaryDrawerState = {
        active: false,
        step: 'summary',
        payload: null,
        submitUrl: null,
        model: null,
    };
    let alternativesDrawerState = {
        active: false,
        lineKey: null,
        alternatives: [],
        selectedIndex: null,
    };
    let orderAmendsHandlersBound = false;
    const orderAmendsItemRegistry = new Map();
    const orderAmendsPendingChanges = new Map();
    const orderAmendsBaselines = new Map();
    const cancellationFlowByLine = new Map();
    const alternativesProductsCache = new Map();

    function t() {
        return global.orderTrackerTranslations || {};
    }

    function getRoot() {
        return document.getElementById('order-tracking');
    }

    // --- Config & pre-checks ---

    function getOrderAmendApiUrl() {
        const raw = (getRoot()?.dataset.orderAmendApiUrl || '').trim();
        return raw.toLowerCase().startsWith('https://') ? raw : '';
    }

    function getCancellationFlowConfig() {
        const el = getRoot();
        const parseIntSetting = (raw, fallback) => {
            const n = parseInt(raw, 10);
            return Number.isFinite(n) ? n : fallback;
        };
        const url = (raw) => {
            const s = (raw || '').trim();
            return s.toLowerCase().startsWith('https://') ? s : '';
        };
        return {
            apiUrl: getOrderAmendApiUrl(),
            deliveryDaysThreshold: parseIntSetting(el?.dataset.cancellationDeliveryDaysThreshold, 14),
            refundDaysMin: parseIntSetting(el?.dataset.cancellationRefundDaysMin, 5),
            refundDaysMax: parseIntSetting(el?.dataset.cancellationRefundDaysMax, 10),
            returnsPolicyUrl: url(el?.dataset.cancellationReturnsPolicyUrl),
            supportUrl: url(el?.dataset.cancellationSupportUrl),
        };
    }

    function getLineContainer(lineKey) {
        return document.querySelector(`[data-oa-line-key="${CSS.escape(lineKey)}"]`);
    }

    function getFlowState(lineKey) {
        if (!cancellationFlowByLine.has(lineKey)) {
            cancellationFlowByLine.set(lineKey, {
                isActive: false,
                reasonScreenVariant: null,
                selectedReason: null,
                pendingReason: null,
                activeRetentionPath: null,
                retentionStep: null,
                preCheckResults: null,
                deliveryDateRetentionChecks: null,
                cancellationResult: null,
                targetQuantity: null,
                removalCompleted: false,
                originalQuantity: null,
                originalGrossAmount: null,
            });
        }
        return cancellationFlowByLine.get(lineKey);
    }

    function calendarDaysBetween(a, b) {
        if (!a || !b || isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
        const ms = Math.abs(a.getTime() - b.getTime());
        return Math.floor(ms / 86400000);
    }

    function deriveDeliveryDateChanged(shipmentOrBatch, thresholdDays) {
        if (!shipmentOrBatch?.hasRevisedDeliveryDates) return false;
        const original = shipmentOrBatch.earliestDeliveryDate;
        const revised = shipmentOrBatch.revisedEarliestDeliveryDate;
        if (!original || !revised) return false;
        return calendarDaysBetween(original, revised) > thresholdDays;
    }

    function isShippingChargeRefunded(shippingTotal, refundTotals) {
        const shipping = parseNumber(shippingTotal);
        if (shipping <= 0) return false;
        if (!Array.isArray(refundTotals) || !refundTotals.length) return false;
        return refundTotals.some(
            (refund) => Math.abs(parseNumber(refund?.amount) - shipping) < 0.01
        );
    }

    function deriveCancellationPreCheckResults(orderData, { shipmentOrBatch, aggregateLines } = {}) {
        const cfg = getCancellationFlowConfig();
        const summary = orderData?.orderSummary;
        let deliveryDateChanged = false;

        if (aggregateLines) {
            orderAmendsItemRegistry.forEach((entry) => {
                if (deriveDeliveryDateChanged(entry.shipmentOrBatch, cfg.deliveryDaysThreshold)) {
                    deliveryDateChanged = true;
                }
            });
        } else if (shipmentOrBatch) {
            deliveryDateChanged = deriveDeliveryDateChanged(
                shipmentOrBatch,
                cfg.deliveryDaysThreshold
            );
        }

        const shippingTotal = parseNumber(summary?.shippingTotal);
        const hasShippingCharge = shippingTotal > 0;
        const shippingChargeRefunded = isShippingChargeRefunded(
            shippingTotal,
            summary?.refundTotals
        );
        const showAvailabilityReason =
            deliveryDateChanged && hasShippingCharge && !shippingChargeRefunded;

        return {
            deliveryDateChanged,
            hasShippingCharge,
            shippingChargeRefunded,
            showAvailabilityReason,
        };
    }

    function deriveCancellationPreChecks(scope, lineKey, orderData) {
        if (scope === 'order') {
            return deriveCancellationPreCheckResults(orderData, { aggregateLines: true });
        }
        // Line scope: delivery-date pre-check uses this line's parent shipmentOrBatch only.
        return deriveCancellationPreCheckResults(orderData, {
            shipmentOrBatch: orderAmendsItemRegistry.get(lineKey)?.shipmentOrBatch,
        });
    }

    function reasonScreenVariantFromPreChecks(preCheckResults) {
        return preCheckResults?.showAvailabilityReason ? 'B' : 'A';
    }

    function getOrderContext() {
        return global.__orderTrackerCachedOrderData || null;
    }

    function getApiCredentials() {
        const apiKey = document.querySelector('#apiKeyContainer')?.dataset.apiKey;
        const order = getOrderContext();

        return {
            clientApiKey: apiKey,
            orderNumber: order?.orderSummary?.orderNo,
            customerEmail: order?.customerInformation?.email,
            shopifyOrderInternalId: order?.orderSummary?.shopifyOrderInternalId ?? null,
        };
    }

    function resolveCancellationLineKey(scope, lineKey) {
        if (lineKey) return lineKey;
        if (scope === 'order') return getRepresentativeLineKey();
        return null;
    }

    function getCancellationReasonForRequest(scope, lineKey) {
        if (scope === 'order') return orderCancelDrawerState.selectedReason;
        if (lineKey) return getFlowState(lineKey).selectedReason;
        return null;
    }

    async function postCancellationFlowAction(action, opts = {}) {
        const scope = opts.scope || 'line';
        const lineKey = resolveCancellationLineKey(scope, opts.lineKey);
        const extra = opts.extra || {};
        const cfg = getCancellationFlowConfig();
        const creds = getApiCredentials();
        const registryEntry = lineKey ? orderAmendsItemRegistry.get(lineKey) : null;

        const body = {
            action,
            source: 'order-tracker-self-service',
            ...creds,
        };

        if (scope === 'order') {
            body.scope = 'order';
            body.cancellationReason = getCancellationReasonForRequest(scope, lineKey);
        }

        if (lineKey) {
            body.line = {
                lineKey,
                itemNumber: registryEntry?.item?.itemNumber,
                shipmentDocumentNo: registryEntry?.shipmentOrBatch?.documentNo,
                shipmentStatus: registryEntry?.status,
                ...(scope === 'line' ? extra : {}),
            };
        }

        if (scope === 'order' && Object.keys(extra).length) {
            Object.assign(body, extra);
        }

        if (!cfg.apiUrl) {
            throw new Error('Cancellation flow API is not configured.');
        }

        const res = await fetch(cfg.apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.errorMessage || data.message || `HTTP ${res.status}`);
        }
        return data;
    }

    function replaceRefundDaysCopy(html, cfg) {
        return String(html)
            .replace(/\{\{\s*refund_days_min\s*\}\}/g, String(cfg.refundDaysMin))
            .replace(/\{\{\s*refund_days_max\s*\}\}/g, String(cfg.refundDaysMax));
    }

    function buildReasonOptions(preCheckResults) {
        const tr = t();
        const options = [
            { id: CANCELLATION_REASON.CHEAPER_BETTER, label: tr.orderAmendsCancelReasonOptionLjInstead },
            { id: CANCELLATION_REASON.ELSEWHERE, label: tr.orderAmendsCancelReasonOptionElsewhere },
            { id: CANCELLATION_REASON.CHANGED_MIND, label: tr.orderAmendsCancelReasonOptionChangedMind },
            { id: CANCELLATION_REASON.PRICE, label: tr.orderAmendsCancelReasonOptionPrice },
        ];

        if (preCheckResults?.showAvailabilityReason) {
            options.unshift({
                id: CANCELLATION_REASON.AVAILABILITY,
                label: tr.orderAmendsCancelReasonOptionAvailability,
            });
        }

        return options.filter((o) => o.label);
    }

    // --- Removal UI state ---

    function setVisiblyHidden(el, hidden) {
        if (!el) return;
        el.classList.toggle('hidden', hidden);
    }

    function syncReasonConfirmUi(container, hasReason) {
        const reasonConfirm = container?.querySelector('[data-oa-reason-confirm]');
        const reasonNext = container?.querySelector('[data-oa-reason-next]');
        if (reasonConfirm) {
            reasonConfirm.disabled = !hasReason;
            reasonConfirm.classList.toggle('order-amends__reason-next-btn--visible', hasReason);
        }
        if (reasonNext) reasonNext.disabled = !hasReason;
    }

    function getReasonLabel(lineKey, reasonId) {
        const container = getLineContainer(lineKey);
        const select = container?.querySelector('[data-oa-reason-select]');
        if (!select || !reasonId) return '';
        const opt = select.querySelector(`option[value="${CSS.escape(reasonId)}"]`);
        return opt?.textContent?.trim() || reasonId;
    }

    function updateLineDisplay(lineKey) {
        const container = getLineContainer(lineKey);
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const flow = getFlowState(lineKey);
        if (!container || !registryEntry) return;

        const maxQty = Math.max(0, parseInt(registryEntry.item.quantity, 10) || 0);
        const pending = orderAmendsPendingChanges.get(lineKey);

        let displayQty;
        if (flow.isActive) {
            displayQty = flow.targetQuantity ?? 0;
        } else if (pending && typeof pending.requestedQuantity === 'number') {
            displayQty = pending.requestedQuantity;
        } else {
            const parsedQty = parseInt(container.querySelector('[data-oa-qty-current]')?.textContent, 10);
            displayQty = Number.isFinite(parsedQty) ? parsedQty : maxQty;
        }

        const unitAmount = parseFloat(container.querySelector('[data-oa-qty]')?.dataset.oaUnitAmount) ||
            (maxQty > 0 ? parseNumber(registryEntry.item.grossAmount) / maxQty : 0);
        const originalGross = flow.originalGrossAmount ?? parseNumber(registryEntry.item.grossAmount);
        const displayGross = unitAmount * displayQty;

        const qtyTemplate = t().orderAmendsQuantityLabelTemplate || 'Quantity: {{ quantity }}';
        const qtyLabel = container.querySelector('[data-oa-qty-label]');
        if (qtyLabel) {
            qtyLabel.textContent = qtyTemplate.replace(/\{\{\s*quantity\s*\}\}/g, String(displayQty));
        }

        const priceEl = container.querySelector('[data-oa-display-price]');
        const originalEl = container.querySelector('[data-oa-display-original]');
        if (priceEl) priceEl.textContent = formatMoney(displayGross);
        if (originalEl) {
            const stagedRefund =
                !flow.isActive && pending && typeof pending.grossAmount === 'number' && pending.grossAmount > 0
                    ? pending.grossAmount
                    : null;
            const liveFullRemovalRefund = flow.isActive && displayQty === 0 ? originalGross : null;
            const refundAmount = stagedRefund ?? liveFullRemovalRefund;

            if (refundAmount != null) {
                originalEl.textContent = '−' + formatMoney(refundAmount);
                setVisiblyHidden(originalEl, false);
            } else {
                originalEl.textContent = '';
                setVisiblyHidden(originalEl, true);
            }
        }

        const triggeredQty = container.querySelector('[data-oa-triggered-qty-display]');
        if (triggeredQty) triggeredQty.textContent = String(displayQty);

        const qtyCurrent = container.querySelector('[data-oa-qty-current]');
        if (qtyCurrent && !flow.isActive) qtyCurrent.textContent = String(displayQty);

        updateSaveChangesButtonState();
    }

    function updateSaveChangesButtonState() {
        const btn = document.getElementById('order-amends-save-changes');
        if (!btn) return;
        btn.disabled = orderAmendsPendingChanges.size === 0;
    }

    function parseNumber(value) {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    }

    function formatMoney(val) {
        if (typeof global.__orderTrackerFormatMoney === 'function') {
            return global.__orderTrackerFormatMoney(val);
        }
        const num = parseFloat(val);
        const safe = Number.isFinite(num) ? num : 0;
        return '£' + safe.toFixed(2);
    }

    function getOrderedQuantityForLine(lineKey) {
        const item = orderAmendsItemRegistry.get(lineKey)?.item;

        return Math.max(0, parseInt(item?.quantity, 10) || 0);
    }

    function computeLineRemoveRefundFields({ item, orderedQuantity, requestedQuantity, reasonId }) {
        const refundedQty = Math.max(0, orderedQuantity - requestedQuantity);
        const lineGross = parseNumber(item?.grossAmount);
        const grossAmount =
            refundedQty > 0
                ? orderedQuantity > 0
                    ? roundMoneyAmount((lineGross / orderedQuantity) * refundedQty)
                    : roundMoneyAmount(lineGross)
                : 0;
        const apiLineKey = String(item?.id ?? '');
        const cancellationReasonId =
            reasonId != null ? themeReasonToCancellationReasonId(reasonId) : null;
        return { apiLineKey, refundedQty, grossAmount, cancellationReasonId };
    }

    function enterTriggeredState(lineKey, targetQuantity) {
        const container = getLineContainer(lineKey);
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const flow = getFlowState(lineKey);
        if (!container || !registryEntry) return;

        flow.isActive = true;
        flow.targetQuantity = targetQuantity;
        flow.originalQuantity = parseInt(registryEntry.item.quantity, 10) || 0;
        flow.originalGrossAmount = parseNumber(registryEntry.item.grossAmount);
        flow.selectedReason = null;
        flow.pendingReason = null;
        flow.activeRetentionPath = null;
        flow.retentionStep = null;
        flow.cancellationResult = null;
        flow.removalCompleted = false;

        setVisiblyHidden(container.querySelector('.shipment__item-product-actions-amend-state'), true);
        setVisiblyHidden(container.querySelector('[data-oa-remove-active]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-summary-step]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-validation-error]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-cancellation-panel]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-reason-step]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-retention-step]'), true);

        const reasonConfirm = container.querySelector('[data-oa-reason-confirm]');
        if (reasonConfirm) reasonConfirm.disabled = true;
        syncReasonConfirmUi(container, false);

        updateLineDisplay(lineKey);
        populateReasonSelect(lineKey);
    }

    function fillReasonSelect(select, preCheckResults) {
        const tr = t();
        const placeholder = tr.orderAmendsRemoveReasonPlaceholder || 'Please select a reason';
        select.innerHTML = `<option value="">${escapeHtml(placeholder)}</option>`;
        buildReasonOptions(preCheckResults).forEach((opt) => {
            select.insertAdjacentHTML(
                'beforeend',
                `<option value="${escapeHtmlAttr(opt.id)}">${escapeHtml(opt.label)}</option>`
            );
        });
        select.value = '';
    }

    function populateReasonSelect(lineKey) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        const tr = t();
        const intro = container?.querySelector('[data-oa-reason-intro]');
        const select = container?.querySelector('[data-oa-reason-select]');
        if (!select) return;

        const variant = flow.reasonScreenVariant || 'A';
        if (intro) {
            intro.textContent =
                variant === 'B'
                    ? tr.orderAmendsRemoveReasonIntroB
                    : tr.orderAmendsRemoveReasonIntroA;
        }

        fillReasonSelect(select, flow.preCheckResults);
    }

    function populateQtyReductionReasonSelect(lineKey) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        const select = container?.querySelector('[data-oa-qty-reason-select]');
        if (!select) return;
        fillReasonSelect(select, flow.preCheckResults);
    }

    function updateQtyStepperLabel(lineKey) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        const tr = t();
        const label = container?.querySelector('[data-oa-qty-stepper-label]');
        if (!label) return;

        if (flow.isActive && flow.targetQuantity > 0) {
            const removed = (flow.originalQuantity || 0) - flow.targetQuantity;
            const template = tr.orderAmendsQtyRemovingTemplate || 'Quantity: Removing {{ qty }}';
            label.textContent = template.replace(/\{\{\s*qty\s*\}\}/g, String(removed));
        } else {
            label.textContent = tr.orderAmendsQtyStepperLabel || 'Quantity';
        }
    }

    function updateQtyMinusIcon(lineKey, currentQty) {
        const wrap = getLineContainer(lineKey)?.querySelector('[data-oa-qty]');
        const minusIcon = wrap?.querySelector('[data-oa-qty-minus-icon]');
        const trashIcon = wrap?.querySelector('[data-oa-qty-trash-icon]');
        if (!minusIcon || !trashIcon) return;
        const showTrash = currentQty <= 1;
        setVisiblyHidden(minusIcon, showTrash);
        setVisiblyHidden(trashIcon, !showTrash);
    }

    function enterQtyReductionState(lineKey, targetQty) {
        const container = getLineContainer(lineKey);
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const flow = getFlowState(lineKey);
        if (!container || !registryEntry) return;

        const orderData = getOrderContext();
        const preCheckResults = deriveCancellationPreChecks('line', lineKey, orderData);
        flow.preCheckResults = preCheckResults;
        flow.reasonScreenVariant = reasonScreenVariantFromPreChecks(preCheckResults);

        flow.isActive = true;
        flow.targetQuantity = targetQty;
        flow.originalQuantity = parseInt(registryEntry.item.quantity, 10) || 0;
        flow.originalGrossAmount = parseNumber(registryEntry.item.grossAmount);
        flow.selectedReason = null;
        flow.pendingReason = null;
        flow.activeRetentionPath = null;
        flow.retentionStep = null;
        flow.cancellationResult = null;
        flow.removalCompleted = false;

        const wrap = container.querySelector('[data-oa-qty]');

        setVisiblyHidden(container.querySelector('[data-oa-idle-remove]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-qty-discard-btn]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-qty-back-btn]'), true);

        setVisiblyHidden(container.querySelector('[data-oa-choose-product]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-qty-reason-field]'), false);

        setVisiblyHidden(container.querySelector('[data-oa-qty-stepper-block]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-qty-next-row]'), true);

        if (wrap) {
            wrap.querySelector('[data-oa-qty-current]').textContent = String(targetQty);
            updateQtyMinusIcon(lineKey, targetQty);

            const plusBtn = wrap.querySelector('[data-oa-qty-step="1"]');
            if (plusBtn) plusBtn.disabled = targetQty >= flow.originalQuantity;
        }

        updateQtyStepperLabel(lineKey);
        populateQtyReductionReasonSelect(lineKey);
        updateLineDisplay(lineKey);

        // Ensure summary step and validation error are hidden
        setVisiblyHidden(container.querySelector('[data-oa-summary-step]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-validation-error]'), true);
    }

    function resetQtyAmendStateUi(lineKey) {
        const container = getLineContainer(lineKey);
        if (!container) return;

        setVisiblyHidden(container.querySelector('[data-oa-idle-remove]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-qty-discard-btn]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-qty-back-btn]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-choose-product]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-qty-reason-field]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-qty-stepper-block]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-qty-next-row]'), true);

        const qtyReasonSelect = container.querySelector('[data-oa-qty-reason-select]');
        if (qtyReasonSelect) qtyReasonSelect.value = '';

        updateQtyStepperLabel(lineKey);
    }

    function exitQtyReductionState(lineKey) {
        const container = getLineContainer(lineKey);
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const flow = getFlowState(lineKey);
        if (!container) return;

        flow.isActive = false;
        flow.targetQuantity = null;
        flow.selectedReason = null;
        flow.pendingReason = null;
        flow.activeRetentionPath = null;
        flow.retentionStep = null;
        flow.cancellationResult = null;

        orderAmendsPendingChanges.delete(lineKey);

        resetQtyAmendStateUi(lineKey);

        const maxQty = parseInt(registryEntry?.item?.quantity, 10) || 0;
        const wrap = container.querySelector('[data-oa-qty]');
        if (wrap) {
            const qtyCurrent = wrap.querySelector('[data-oa-qty-current]');
            if (qtyCurrent) qtyCurrent.textContent = String(maxQty);
            updateQtyMinusIcon(lineKey, maxQty);
            const plusBtn = wrap.querySelector('[data-oa-qty-step="1"]');
            if (plusBtn) plusBtn.disabled = true;
        }

        clearLineValidationError(lineKey);
        updateLineDisplay(lineKey);
    }

    function escapeHtml(value) {
        if (value == null) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function escapeHtmlAttr(value) {
        return escapeHtml(value);
    }

    function exitTriggeredState(lineKey, { removalCompleted = false } = {}) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        if (!container) return;

        if (removalCompleted || flow.removalCompleted) {
            cancellationFlowByLine.delete(lineKey);
            if (flow.selectedReason) {
                stageLineRemovePayload(lineKey, flow.selectedReason);
            } else {
                orderAmendsPendingChanges.set(lineKey, {
                    requestedQuantity: 0,
                    cancelEntireLine: true,
                    removalReason: flow.selectedReason,
                });
            }
            return;
        }

        // If coming from qty-reduction path, delegate to its own cleanup
        const wasQtyReduction = typeof flow.targetQuantity === 'number' && flow.targetQuantity > 0;

        flow.isActive = false;
        flow.targetQuantity = null;
        flow.selectedReason = null;
        flow.pendingReason = null;
        flow.activeRetentionPath = null;
        flow.retentionStep = null;
        flow.cancellationResult = null;

        orderAmendsPendingChanges.delete(lineKey);

        setVisiblyHidden(container.querySelector('.shipment__item-product-actions-amend-state'), false);
        setVisiblyHidden(container.querySelector('[data-oa-remove-active]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-cancellation-panel]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-reason-step]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-retention-step]'), true);
        setVisiblyHidden(container.querySelector('[data-oa-summary-step]'), true);
        clearLineValidationError(lineKey);

        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        if (registryEntry) {
            const maxQty = flow.originalQuantity ?? (parseInt(registryEntry.item.quantity, 10) || 0);
            const wrap = container.querySelector('[data-oa-qty]');
            if (wrap) {
                const qtyCurrent = wrap.querySelector('[data-oa-qty-current]');
                if (qtyCurrent) qtyCurrent.textContent = String(maxQty);
                updateQtyMinusIcon(lineKey, maxQty);
                const plusBtn = wrap.querySelector('[data-oa-qty-step="1"]');
                if (plusBtn) plusBtn.disabled = true;
            }
        }

        // Restore idle amend-state internals if returning from qty-reduction path
        if (wasQtyReduction) resetQtyAmendStateUi(lineKey);

        updateLineDisplay(lineKey);
    }

    function revealLineRetentionContainer(container) {
        setVisiblyHidden(container?.querySelector('[data-oa-remove-active]'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-cancellation-panel]'), false);
    }

    function showRetentionPanel(lineKey, html) {
        const container = getLineContainer(lineKey);
        revealLineRetentionContainer(container);
        const reasonStep = container?.querySelector('[data-oa-reason-step]');
        const retentionStep = container?.querySelector('[data-oa-retention-step]');
        setVisiblyHidden(reasonStep, true);
        setVisiblyHidden(retentionStep, false);
        setVisiblyHidden(container?.querySelector('[data-oa-summary-step]'), true);
        if (retentionStep) retentionStep.innerHTML = html;
    }

    function showReasonPanel(lineKey) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        flow.retentionStep = 'reason';
        flow.selectedReason = null;
        flow.pendingReason = null;
        setVisiblyHidden(container?.querySelector('[data-oa-reason-step]'), false);
        setVisiblyHidden(container?.querySelector('[data-oa-retention-step]'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-summary-step]'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-reason-actions]'), true);
        const select = container?.querySelector('[data-oa-reason-select]');
        const reasonNext = container?.querySelector('[data-oa-reason-next]');
        if (select) select.value = '';
        if (reasonNext) reasonNext.disabled = true;
    }

    function buildStagedRemoveLinePayload(lineKey, reasonId) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const flow = getFlowState(lineKey);
        if (!registryEntry) return null;

        const item = registryEntry.item || {};
        const orderedQuantity = getOrderedQuantityForLine(lineKey);
        const requestedQuantity =
            typeof flow.targetQuantity === 'number' ? flow.targetQuantity : 0;
        const { apiLineKey, refundedQty, grossAmount, cancellationReasonId } =
            computeLineRemoveRefundFields({
                item,
                orderedQuantity,
                requestedQuantity,
                reasonId,
            });

        if (!apiLineKey) {
            console.warn('[Order amends] Missing API item id for', lineKey);
            return null;
        }

        return {
            requestedQuantity,
            cancelEntireLine: requestedQuantity === 0,
            removalReason: reasonId,
            lineKey: apiLineKey,
            cancellationReasonId,
            refundedQty,
            grossAmount,
        };
    }

    function stageLineRemovePayload(lineKey, reasonId) {
        const payload = buildStagedRemoveLinePayload(lineKey, reasonId);
        if (!payload) return false;
        orderAmendsPendingChanges.set(lineKey, payload);
        return true;
    }

    function showRemovalSummaryStep(lineKey) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        const tr = t();

        setVisiblyHidden(container?.querySelector('[data-oa-remove-active]'), true);
        setVisiblyHidden(container?.querySelector('.shipment__item-product-actions-amend-state'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-reason-step]'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-retention-step]'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-cancellation-panel]'), true);
        setVisiblyHidden(container?.querySelector('[data-oa-summary-step]'), false);
        clearLineValidationError(lineKey);

        const summaryEl = container?.querySelector('[data-oa-summary-text]');
        if (summaryEl) {
            const isPartialReduction = typeof flow.targetQuantity === 'number' && flow.targetQuantity > 0;
            if (isPartialReduction) {
                const removed = (flow.originalQuantity || 0) - flow.targetQuantity;
                const remaining = flow.targetQuantity;
                const summaryHtml = (tr.orderAmendsQtyReducedSummary || 'Quantity reduced: {{ removed }} product removed, {{ remaining }} remaining')
                    .replace(/\{\{\s*removed\s*\}\}/g, String(removed))
                    .replace(/\{\{\s*remaining\s*\}\}/g, String(remaining));
                summaryEl.innerHTML = summaryHtml;
            } else {
                const reasonLabel = getReasonLabel(lineKey, flow.selectedReason);
                const summaryHtml = (tr.orderAmendsRemoveSummaryReasonHtml || 'Removal Reason: {{ reason }}').replace(
                    /\{\{\s*reason\s*\}\}/g,
                    escapeHtml(reasonLabel)
                );
                summaryEl.innerHTML = summaryHtml;
            }
        }

        const qtyCurrent = container?.querySelector('[data-oa-qty-current]');
        if (qtyCurrent) qtyCurrent.textContent = String(flow.targetQuantity ?? 0);

        flow.retentionStep = 'summary';
        flow.isActive = false;
        updateLineDisplay(lineKey);
    }

    function isValidDrawerLinkHref(href) {
        const s = (href || '').trim();
        return s.toLowerCase().startsWith('https://');
    }

    function getDrawerButtonClass(b, variant) {
        if (b.primary) return 'button--primary';
        if (variant === 'outline') return 'button--outline';
        return 'button--secondary';
    }

    function renderDrawerButton(
        b,
        { variant = 'secondary', actionAttr = 'data-oa-order-cancel-action' } = {}
    ) {
        const btnClass = getDrawerButtonClass(b, variant);
        const classes = `button ${btnClass}`;
        const label = b.label || '';
        const href = (b.href || '').trim();

        if (isValidDrawerLinkHref(href)) {
            const target = b.target === '_blank' ? '_blank' : '_self';
            const targetAttr =
                target === '_blank' ? ' target="_blank" rel="noopener noreferrer"' : '';
            return `<a href="${escapeHtmlAttr(href)}" class="${classes}"${targetAttr}>${label}</a>`;
        }

        const action = b.action
            ? `${actionAttr}="${escapeHtmlAttr(b.action)}"`
            : '';
        const disabled =
            b.disabled || (b.href !== undefined && !isValidDrawerLinkHref(href)) ? ' disabled' : '';
        const inner =
            actionAttr === 'data-oa-retention-action' ? `<span>${label}</span>` : label;
        return `<button type="button" class="${classes}" ${action}${disabled}>${inner}</button>`;
    }

    function buildContactSupportButton(overrides = {}) {
        const tr = t();
        const cfg = getCancellationFlowConfig();
        return {
            action: 'contact-support',
            label: tr.orderAmendsCancellationContactSupport || 'Contact Support',
            href: cfg.supportUrl,
            target: '_blank',
            ...overrides,
        };
    }

    function buildReturnsPolicyButton(overrides = {}) {
        const tr = t();
        const cfg = getCancellationFlowConfig();
        return {
            action: 'returns-policy',
            label: tr.orderAmendsCancellationReturnsPolicy || 'Returns Policy',
            href: cfg.returnsPolicyUrl,
            target: '_self',
            ...overrides,
        };
    }

    function retentionPanelHtml({ title, message, messageHtml, buttons, centered }) {
        const btnHtml = (buttons || [])
            .map((b) =>
                renderDrawerButton(b, {
                    variant: 'secondary',
                    actionAttr: 'data-oa-retention-action',
                })
            )
            .join('');
        const messageContent = messageHtml != null ? messageHtml : message || '';
        const messageClass = centered
            ? 'order-amends__retention-message order-amends__retention-message--centered'
            : 'order-amends__retention-message';
        const panelClass = centered
            ? 'order-amends__retention-panel order-amends__retention-panel--centered'
            : 'order-amends__retention-panel';
        const messageEl = centered
            ? `<div class="${messageClass}"><p>${messageContent}</p></div>`
            : `<p class="${messageClass}">${messageContent}</p>`;
        return `
            <div class="${panelClass}">
                ${title ? `<h4 class="order-amends__retention-title">${title}</h4>` : ''}
                ${messageEl}
                ${buttons?.length ? `<div class="button-group">${btnHtml}</div>` : ''}
            </div>`;
    }

    function showLoadingPanel(lineKey) {
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: '',
                message: escapeHtml(t().orderAmendsCancellationProcessing || 'Processing…'),
                buttons: [],
            })
        );
    }

    async function runCancellationPreChecks(opts) {
        const scope = opts?.scope || 'line';
        const lineKey = opts?.lineKey;
        const orderData = getOrderContext();
        const preCheckResults = deriveCancellationPreChecks(scope, lineKey, orderData);
        const variant = reasonScreenVariantFromPreChecks(preCheckResults);
        if (scope === 'order') {
            orderCancelDrawerState.preCheckResults = preCheckResults;
            orderCancelDrawerState.reasonScreenVariant = variant;
        } else {
            const flow = getFlowState(lineKey);
            flow.preCheckResults = preCheckResults;
            flow.reasonScreenVariant = variant;
        }
        return preCheckResults;
    }

    async function startRemovalFlow(lineKey, targetQuantity) {
        const flow = getFlowState(lineKey);
        if (flow.isActive) return;

        await runCancellationPreChecks({ scope: 'line', lineKey });
        enterTriggeredState(lineKey, targetQuantity);
    }

    // --- Retention flows (see docs/cancellation_flow.md) ---

    function showPriceRetentionSuccessPanel(lineKey, refundAmount) {
        const tr = t();
        const cfg = getCancellationFlowConfig();
        const amount = formatMoney(refundAmount);
        const msg = replaceRefundDaysCopy(
            (tr.orderAmendsCancellationPriceSuccessMessageHtml || '').replace(/\{\{\s*amount\s*\}\}/g, amount),
            cfg
        );
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: tr.orderAmendsCancellationPriceSuccessTitle || 'Retain',
                message: msg
            })
        );
    }

    function showPriceRetentionFailurePanel(lineKey, { includeContactSupport = true } = {}) {
        const tr = t();
        const buttons = includeContactSupport
            ? [
                  buildContactSupportButton(),
                  { action: 'done', label: tr.orderAmendsCancellationDone || 'Done', primary: true },
              ]
            : [{ action: 'done', label: tr.orderAmendsCancellationDone || 'Done', primary: true }];
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: tr.orderAmendsCancellationPriceFailureTitle || 'Failure',
                message: escapeHtml(tr.orderAmendsCancellationPriceFailureMessage || ''),
                buttons,
            })
        );
    }

    function showLineCancellationErrorPanel(lineKey) {
        const tr = t();
        const flow = getFlowState(lineKey);
        flow.cancellationResult = 'error';
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: '',
                message: escapeHtml(
                    tr.orderAmendsSaveChangesError ||
                        'We could not save your changes. Please try again or contact customer service.'
                ),
                buttons: [
                    { action: 'done', label: tr.orderAmendsCancellationDone || 'Done', primary: true },
                ],
                centered: true,
            })
        );
    }

    function showShippingRefundOfferPanel(lineKey, reasonLabel) {
        const tr = t();
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: reasonLabel || tr.orderAmendsCancelReasonOptionAvailability || 'Delivery date',
                message: escapeHtml(
                    tr.orderAmendsCancellationShippingOfferMessageV2 ||
                        tr.orderAmendsCancellationShippingOfferMessage ||
                        ''
                ),
                buttons: [
                    {
                        action: 'accept-shipping-refund',
                        label:
                            tr.orderAmendsCancellationAcceptAndKeep ||
                            tr.orderAmendsCancellationAcceptShippingRefund ||
                            'Accept & Keep',
                        primary: true,
                    },
                    {
                        action: 'continue-removing',
                        label: tr.orderAmendsCancellationContinueRemoving || 'Continue Removal',
                    },
                ],
                centered: true,
            })
        );
    }

    function showCancellationInProgressPanel(lineKey) {
        const tr = t();
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: tr.orderAmendsCancellationInProgressTitle || 'Cancellation Focus',
                message: escapeHtml(tr.orderAmendsCancellationInProgressMessage || ''),
                buttons: [buildReturnsPolicyButton()],
            })
        );
    }

    function showCancellationSuccessPanel(lineKey, refundAmount) {
        const tr = t();
        const cfg = getCancellationFlowConfig();
        const amount = formatMoney(refundAmount);
        const msg = replaceRefundDaysCopy(
            (tr.orderAmendsCancellationSuccessMessageHtml || '').replace(/\{\{\s*amount\s*\}\}/g, amount),
            cfg
        );
        showRetentionPanel(
            lineKey,
            retentionPanelHtml({
                title: tr.orderAmendsCancellationSuccessTitle || 'Cancellation Success',
                message: msg
            })
        );
    }

    function createLineCancellationSink(lineKey) {
        const showError = () => showLineCancellationErrorPanel(lineKey);

        return {
            showLoading: () => showLoadingPanel(lineKey),
            showPriceSuccess: (refundAmount) => showPriceRetentionSuccessPanel(lineKey, refundAmount),
            showPriceFailure: (opts) => showPriceRetentionFailurePanel(lineKey, opts),
            showShippingOffer: (reasonLabel) => showShippingRefundOfferPanel(lineKey, reasonLabel),
            showInProgress: () => showCancellationInProgressPanel(lineKey),
            showSuccess: (refundAmount) => showCancellationSuccessPanel(lineKey, refundAmount),
            showShippingSuccess: (refundAmount) => {
                const tr = t();
                const cfg = getCancellationFlowConfig();
                const amount = formatMoney(refundAmount);
                const msg = replaceRefundDaysCopy(
                    (tr.orderAmendsCancellationShippingSuccessMessageHtml || '').replace(
                        /\{\{\s*amount\s*\}\}/g,
                        amount
                    ),
                    cfg
                );
                showRetentionPanel(
                    lineKey,
                    retentionPanelHtml({
                        title: tr.orderAmendsCancellationShippingAppliedTitle || 'Shipping refund applied',
                        messageHtml: msg,
                        centered: true,
                        buttons: [
                            {
                                action: 'done',
                                label: tr.orderAmendsCancellationDone || 'Done',
                                primary: true,
                            },
                        ],
                    })
                );
            },
            onError: showError,
            onShippingRefundError: showError,
        };
    }

    function createOrderCancellationSink() {
        return {
            showLoading: () => showOrderCancelLoadingPanel(),
            showPriceSuccess: (refundAmount) => showOrderCancelPriceSuccessPanel(refundAmount),
            showPriceFailure: () => showOrderCancelPriceFailurePanel(),
            showShippingOffer: () => showOrderCancelShippingOfferPanel(),
            showInProgress: () => showOrderCancelInProgressPanel(),
            showSuccess: (refundAmount) => showOrderCancelSuccessPanel(refundAmount),
            showShippingSuccess: (refundAmount) => showOrderCancelShippingSuccessPanel(refundAmount),
            onShippingRefundError: () => {
                showOrderCancelGenericErrorPanel();
            },
        };
    }

    function createLineCancellationCtx(lineKey) {
        return {
            scope: 'line',
            lineKey,
            getState: () => getFlowState(lineKey),
            postAction: (action, extra) =>
                postCancellationFlowAction(action, { scope: 'line', lineKey, extra }),
            sink: createLineCancellationSink(lineKey),
            getShippingOfferReasonLabel: () =>
                getReasonLabel(lineKey, getFlowState(lineKey).selectedReason),
        };
    }

    function createOrderCancellationCtx() {
        return {
            scope: 'order',
            lineKey: null,
            getState: () => orderCancelDrawerState,
            postAction: (action, extra) =>
                postCancellationFlowAction(action, { scope: 'order', extra }),
            sink: createOrderCancellationSink(),
            getShippingOfferReasonLabel: () =>
                getOrderCancelReasonLabel(orderCancelDrawerState.selectedReason),
        };
    }

    async function runPriceRetention(ctx) {
        ctx.sink.showLoading();
        const state = ctx.getState();

        try {
            const res = await ctx.postAction('validatePriceChange');
            if (res.status === 'success') {
                state.cancellationResult = 'priceChangeSuccess';
                ctx.sink.showPriceSuccess(res.refundAmount);
            } else {
                state.cancellationResult = 'priceChangeFailed';
                ctx.sink.showPriceFailure({});
            }
        } catch (err) {
            console.error(err);
            state.cancellationResult = 'priceChangeFailed';
            if (ctx.scope === 'line' && ctx.sink.onError) {
                ctx.sink.onError();
            } else {
                ctx.sink.showPriceFailure({ includeContactSupport: false });
            }
        }
    }

    async function runDeliveryRetention(ctx) {
        const state = ctx.getState();

        if (ctx.scope === 'order') {
            ctx.sink.showLoading();
            state.cancellationResult = 'shippingRefundOffered';
            state.activeRetentionPath = 'deliveryDate';
            ctx.sink.showShippingOffer();
            return;
        }

        state.cancellationResult = 'shippingRefundOffered';
        state.activeRetentionPath = 'deliveryDate';
        state.retentionStep = 'shipping-offer';
        ctx.sink.showShippingOffer(ctx.getShippingOfferReasonLabel());
    }

    async function runAcceptShippingRefund(ctx) {
        ctx.sink.showLoading();
        const state = ctx.getState();
        try {
            if (ctx.scope === 'order') {
                const res = await postOrderShippingRefund();
                state.cancellationResult = 'shippingRefundAccepted';
                const refundAmount =
                    res.refundAmount ??
                    parseFloat(getOrderContext()?.orderSummary?.shippingTotal || 0);
                refetchOrderAfterCancel();
                ctx.sink.showShippingSuccess(refundAmount);
                return;
            }
            const res = await ctx.postAction('acceptShippingRefund');
            state.cancellationResult = 'shippingRefundAccepted';
            refetchOrderAfterCancel();
            ctx.sink.showShippingSuccess(res.refundAmount);
        } catch (err) {
            console.error(err);
            if (ctx.sink.onError) {
                ctx.sink.onError();
            } else if (ctx.sink.onShippingRefundError) {
                ctx.sink.onShippingRefundError();
            }
        }
    }

    async function routeCancellationAfterReason(ctx, reasonId) {
        const state = ctx.getState();
        state.selectedReason = reasonId;

        const branch = REASON_BRANCH[reasonId];
        if (branch === 'price') {
            if (ctx.scope === 'order') {
                state.activeRetentionPath = 'directCancel';
                ctx.sink.showLoading();
                await completeOrderCancelFromDrawer();
                return;
            }
            state.activeRetentionPath = 'price';
            await runPriceRetention(ctx);
            return;
        }
        if (branch === 'deliveryDate') {
            state.activeRetentionPath = 'deliveryDate';
            await runDeliveryRetention(ctx);
            return;
        }
        if (isNoRetentionReason(ctx.scope, reasonId)) {
            state.activeRetentionPath = 'directCancel';
            if (ctx.scope === 'order') {
                ctx.sink.showLoading();
                await completeOrderCancelFromDrawer();
            }
        }
    }

    async function acceptShippingRefund(lineKey) {
        await runAcceptShippingRefund(createLineCancellationCtx(lineKey));
    }

    function handleRetentionAction(lineKey, action) {
        const flow = getFlowState(lineKey);

        switch (action) {
            case 'back-reason':
                flow.selectedReason = null;
                showReasonPanel(lineKey);
                break;
            case 'accept-shipping-refund':
                acceptShippingRefund(lineKey);
                return;
            case 'continue-removing':
                if (flow.selectedReason) {
                    stageLineRemovePayload(lineKey, flow.selectedReason);
                }
                showRemovalSummaryStep(lineKey);
                return;
            case 'done':
                exitTriggeredState(lineKey);
                break;
            default:
                exitTriggeredState(lineKey);
        }
    }

    // --- Cancel entire order (drawer + cancellation_flow.md) ---

    function getOrderTrackerDrawer() {
        return document.querySelector('order-tracker-drawer');
    }

    function getOrderCancelApiUrl() {
        const raw = getOrderAmendApiUrl();
        if (!raw) return '';
        const apiKey = getApiCredentials().clientApiKey;

        if (apiKey) {
            try {
                const url = new URL(raw);
                url.searchParams.set('clientApiKey', apiKey);
                return url.toString();
            } catch {
                return raw;
            }
        }

        return raw;
    }

    function resetOrderCancelDrawerState() {
        orderCancelFlowActive = false;
        orderCancelDrawerState = {
            step: 'reason',
            selectedReason: null,
            pendingReason: null,
            pendingShippingRetentionChoice: null,
            reasonScreenVariant: 'B',
            preCheckResults: null,
            activeRetentionPath: null,
            cancellationResult: null,
            deliveryDateRetentionChecks: null,
        };
    }

    function getRepresentativeLineKey() {
        const first = orderAmendsItemRegistry.keys().next();
        return first.done ? null : first.value;
    }

    function buildOrderCancelReasonOptions(preCheckResults) {
        const tr = t();
        const options = [
            { id: CANCELLATION_REASON.LJ_INSTEAD, label: tr.orderAmendsCancelReasonOptionLjInstead },
            { id: CANCELLATION_REASON.ELSEWHERE, label: tr.orderAmendsCancelReasonOptionElsewhere },
            { id: CANCELLATION_REASON.PRICE, label: tr.orderAmendsCancelReasonOptionPrice },
            { id: CANCELLATION_REASON.CHANGED_MIND, label: tr.orderAmendsCancelReasonOptionChangedMind },
        ];
        if (preCheckResults?.showAvailabilityReason) {
            options.unshift({
                id: CANCELLATION_REASON.AVAILABILITY,
                label: tr.orderAmendsCancelReasonOptionAvailability,
            });
        }
        return options.filter((o) => o.label);
    }

    function getOrderCancelReasonLabel(reasonId) {
        const state = orderCancelDrawerState;
        const opt = buildOrderCancelReasonOptions(state.preCheckResults).find((o) => o.id === reasonId);
        return opt?.label || reasonId || '';
    }

    function orderCancelSelectedReasonSubheaderHtml() {
        const label = getOrderCancelReasonLabel(orderCancelDrawerState.selectedReason);
        if (!label) return '';
        return `<div class="order-tracker-drawer__sub-header"><p>${escapeHtml(label)}</p></div>`;
    }

    function orderCancelCloseFooterButton() {
        return {
            action: 'close',
            label: t().orderAmendsCancelReasonClose || t().orderAmendsCancellationCloseExit || 'Close',
        };
    }

    function orderCancelFooterButtons(extraButtons = []) {
        return [...extraButtons, orderCancelCloseFooterButton()];
    }

    function orderCancelFooterHtml(buttons) {
        return `<div class="order-details__cancel-reason-footer button-group">${(buttons || [])
            .map((b) => renderDrawerButton(b, { variant: 'outline' }))
            .join('')}</div>`;
    }

    function orderCancelRetentionBodyHtml({ title, message, messageHtml, innerButtons, centered }) {
        const btnHtml = (innerButtons || [])
            .map((b) => renderDrawerButton(b, { variant: 'secondary' }))
            .join('');
        const messageContent = messageHtml != null ? messageHtml : message || '';
        const messageClass = centered
            ? 'order-amends__retention-message order-amends__retention-message--centered'
            : 'order-amends__retention-message';
        const panelClass = centered
            ? 'order-amends__retention-panel order-amends__retention-panel--centered'
            : 'order-amends__retention-panel';
        return `
            <div class="${panelClass}">
                ${title ? `<h4 class="order-amends__retention-title">${escapeHtml(title)}</h4>` : ''}
                <div class="${messageClass}">${messageContent}</div>
                ${innerButtons?.length ? `<div class="button-group">${btnHtml}</div>` : ''}
            </div>`;
    }

    function openOrderCancelDrawerContent(title, rootHtml) {
        const drawer = getOrderTrackerDrawer();
        if (!drawer) return false;
        drawer.open({
            title: title || t().orderAmendsCancelReasonTitle || 'Cancellation',
            html: rootHtml,
        });
        return true;
    }

    function renderOrderCancelReasonStep() {
        const tr = t();
        const state = orderCancelDrawerState;

        const selected = state.pendingReason || state.selectedReason || '';
        const optionsHtml = buildOrderCancelReasonOptions(state.preCheckResults)
            .map(
                (opt) => `
            <label class="order-details__cancel-reason-option">
                <input type="radio" class="order-details__cancel-reason-input" name="order-cancel-reason" value="${escapeHtmlAttr(opt.id)}"${selected === opt.id ? ' checked' : ''} />
                <span class="order-details__cancel-reason-label">${escapeHtml(opt.label)}</span>
            </label>`
            )
            .join('');

        const rootHtml = `
            <div class="order-cancel-reason-drawer-root">
                <div class="order-tracker-drawer__sub-header">
                    <p>Tell us more about why you would like to cancel your order</p>
                </div>
                <fieldset class="order-details__cancel-reason-fieldset">
                    <legend class="visually-hidden">${escapeHtml(tr.orderAmendsCancelReasonLegend || 'Select a reason')}</legend>
                    ${optionsHtml}
                </fieldset>
                ${orderCancelFooterHtml(
                    orderCancelFooterButtons([
                        {
                            action: 'next',
                            label: tr.orderAmendsCancelReasonNext || 'Next',
                            primary: true,
                            disabled: !selected,
                        },
                    ])
                )}
            </div>`;

        state.step = 'reason';
        openOrderCancelDrawerContent(tr.orderAmendsCancelReasonTitle, rootHtml);
    }

    function renderOrderCancelRetentionPanel({ title, message, messageHtml, buttons, centered }) {
        const body = orderCancelRetentionBodyHtml({
            title,
            message,
            messageHtml,
            innerButtons: buttons || [],
            centered,
        });
        const subheader = orderCancelSelectedReasonSubheaderHtml();
        const rootHtml = `<div class="order-cancel-reason-drawer-root">${subheader}${body}${orderCancelFooterHtml(orderCancelFooterButtons())}</div>`;
        orderCancelDrawerState.step = 'retention';
        openOrderCancelDrawerContent(t().orderAmendsCancelReasonTitle, rootHtml);
    }

    function renderOrderCancelShippingRetentionStep() {
        const tr = t();
        const state = orderCancelDrawerState;
        const selected = state.pendingShippingRetentionChoice || '';
        const options = [
            {
                id: 'accept-shipping-refund',
                label:
                    tr.orderAmendsCancelAcceptShippingRefund ||
                    tr.orderAmendsCancellationAcceptShippingRefund ||
                    'Accept a shipping refund',
            },
            {
                id: 'continue-cancelling',
                label: tr.orderAmendsCancellationContinueCancelling || 'Continue cancelling order',
            },
        ];
        const optionsHtml = options
            .map(
                (opt) => `
            <label class="order-details__cancel-reason-option">
                <input type="radio" class="order-details__cancel-reason-input" name="order-cancel-shipping-retention" value="${escapeHtmlAttr(opt.id)}"${selected === opt.id ? ' checked' : ''} />
                <span class="order-details__cancel-reason-label">${escapeHtml(opt.label)}</span>
            </label>`
            )
            .join('');

        const rootHtml = `
            <div class="order-cancel-reason-drawer-root">
                ${orderCancelSelectedReasonSubheaderHtml()}
                <fieldset class="order-details__cancel-reason-fieldset order-details__cancel-reason-fieldset--retention">
                    <div class="order-amends__retention-message order-amends__retention-message--centered"><p>${escapeHtml(
                        tr.orderAmendsCancelShippingRefundIntro || 'Claim a shipping refund to keep this order.'
                    )}</p></div>
                    <legend class="visually-hidden">${escapeHtml('Choose an option')}</legend>
                    <div class="order-details__cancel-reason-options">${optionsHtml}</div>
                </fieldset>
                ${orderCancelFooterHtml([
                    {
                        action: 'next',
                        label: tr.orderAmendsCancelReasonNext || 'Next',
                        primary: true,
                        disabled: !selected,
                    },
                    {
                        action: 'back-reason',
                        label: tr.orderAmendsCancellationBack || 'Back',
                    },
                ])}
            </div>`;

        state.step = 'shipping-retention';
        openOrderCancelDrawerContent(tr.orderAmendsCancelReasonTitle, rootHtml);
    }

    function buildOrderCancelCustomerSupportLink(cfg, { target = '_self' } = {}) {
        const label = 'Customer Support';
        if (cfg.supportUrl) {
            const targetAttr =
                target === '_blank' ? ' target="_blank" rel="noopener noreferrer"' : '';
            return `<a href="${escapeHtmlAttr(cfg.supportUrl)}" class="order-amends__support-link"${targetAttr}>${escapeHtml(label)}</a>`;
        }
        return `<span class="order-amends__support-link">${escapeHtml(label)}</span>`;
    }

    function getOrderCancelResponseFailureConfig(reasonId) {
        const tr = t();
        const cfg = getCancellationFlowConfig();

        if (reasonId === CANCELLATION_REASON.PRICE) {
            return {
                messageHtml: escapeHtml(
                    tr.orderAmendsCancelOrderResponsePriceHtml ||
                        'We were unable to confirm a price change for this product. If you would like to continue with your cancellation speak to a member of our Customer Support team for further assistance.'
                ),
                buttons: [buildContactSupportButton({ primary: false })],
                centered: true,
            };
        }

        const supportLink = buildOrderCancelCustomerSupportLink(cfg);
        const inProgressHtml = (
            tr.orderAmendsCancelOrderResponseInProgressHtml ||
            '<p>Your shipment is currently being prepared in our warehouse and cannot be automatically cancelled.</p><p>You can contact our {{ support_link }} service for further assistance, or request a return once your shipment has been received.</p>'
        ).replace(/\{\{\s*support_link\s*\}\}/g, supportLink);

        return {
            messageHtml: inProgressHtml,
            buttons: [buildReturnsPolicyButton()],
            centered: true,
        };
    }

    function showOrderCancelGenericErrorPanel() {
        const tr = t();
        orderCancelDrawerState.cancellationResult = 'error';
        renderOrderCancelRetentionPanel({
            title: '',
            message:
                tr.orderAmendsCancelOrderApiErrorMessage ||
                    "We\'re sorry, but something went wrong while processing your request. Please try again later. If the issue continues, contact our Customer Support team for further assistance.",
            buttons: [buildContactSupportButton({ primary: true })],
            centered: true,
        });
    }

    function showOrderCancelResponseFailurePanel(reasonId) {
        const config = getOrderCancelResponseFailureConfig(reasonId);
        orderCancelDrawerState.cancellationResult = 'error';
        renderOrderCancelRetentionPanel({
            title: '',
            messageHtml: config.messageHtml,
            buttons: config.buttons,
            centered: config.centered,
        });
    }

    function showOrderCancelSuccessPanelForOutcome({ reasonId, result, refundSummary }) {
        const tr = t();
        const apiAmount = result?.refundAmount;
        const amount =
            apiAmount != null && Number.isFinite(parseFloat(apiAmount))
                ? parseFloat(apiAmount)
                : refundSummary?.refundTotal ?? 0;
        const productListHtml = buildOrderCancelProductListHtml(refundSummary?.productLines);
        const messageHtml = buildOrderCancelSuccessMessageHtml('full', {
            amount,
            productListHtml,
        });

        orderCancelDrawerState.cancellationResult = 'success';

        renderOrderCancelRetentionPanel({
            title: '',
            messageHtml,
            buttons: [],
            centered: true,
        });
    }

    function handleOrderCancelApiFailure(err) {
        console.error(err);
        const reasonId = orderCancelDrawerState.selectedReason;
        if (err?.name === 'OrderCancelResponseFailure') {
            showOrderCancelResponseFailurePanel(reasonId);
        } else {
            showOrderCancelGenericErrorPanel();
        }
    }

    function showOrderCancelLoadingPanel() {
        renderOrderCancelRetentionPanel({
            title: '',
            messageHtml: cancellationLoadingMessageHtml(),
            buttons: [],
        });
        orderCancelDrawerState.step = 'loading';
    }

    function cancellationLoadingMessageHtml() {
        return `
            <div class="order-tracker-drawer__loading-message">
                <div class="order-tracker-drawer__loading-spinner loading-spinner">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" class="spinner">
                        <path d="M20.9 10.9492C21.5075 10.9492 22.0057 11.4429 21.945 12.0474C21.7657 13.835 21.1505 15.5574 20.1462 17.0605C18.9375 18.8694 17.2195 20.2793 15.2095 21.1119C13.1995 21.9445 10.9878 22.1623 8.85401 21.7379C6.72022 21.3134 4.7602 20.2658 3.22183 18.7274C1.68345 17.189 0.635799 15.229 0.211362 13.0952C-0.213076 10.9614 0.0047612 8.74969 0.837325 6.7397C1.66989 4.72971 3.07979 3.01175 4.88873 1.80305C6.39177 0.798751 8.11422 0.183524 9.90179 0.00416892C10.5063 -0.0564808 11 0.441705 11 1.04922C11 1.65673 10.5056 2.14213 9.90283 2.21788C8.55114 2.38773 7.25203 2.86986 6.11098 3.63229C4.66383 4.59924 3.53591 5.97361 2.86986 7.5816C2.20381 9.18959 2.02954 10.959 2.36909 12.666C2.70864 14.373 3.54676 15.9411 4.77746 17.1718C6.00816 18.4025 7.57617 19.2406 9.28321 19.5801C10.9902 19.9197 12.7596 19.7454 14.3676 19.0794C15.9756 18.4133 17.35 17.2854 18.3169 15.8382C19.0794 14.6972 19.5615 13.3981 19.7313 12.0464C19.8071 11.4436 20.2925 10.9492 20.9 10.9492Z" fill="#5C5F5C"/>
                    </svg>
                </div>
                <p class="order-tracker-drawer__loading-message-text">Processing your request</p>
            </div>
        `
    }

    function refetchOrderAfterCancel() {
        if (typeof global.__orderTrackerRefetchOrder === 'function') {
            global.__orderTrackerRefetchOrder();
        }
    }

    /**
     * ShopifyOrderStatus envelope: top-level `success` plus nested `data.success`.
     * HTTP 2xx may still mean failure when `data.success === false`.
     */
    function createOrderCancelResponseFailure(message) {
        const err = new Error(message || 'Request failed.');
        err.name = 'OrderCancelResponseFailure';
        return err;
    }

    function parseShopifyOrderStatusResponse(data, fallbackError) {
        const payload =
            data?.data != null && typeof data.data === 'object' ? data.data : data;
        const innerFailed = payload?.success === false;
        const outerFailed = data?.success === false && payload?.success !== true;

        if (innerFailed || outerFailed) {
            const msg =
                payload?.errorMessage ||
                payload?.message ||
                data?.errorMessage ||
                data?.message ||
                fallbackError ||
                'Request failed.';
            throw createOrderCancelResponseFailure(
                String(msg).trim() || fallbackError || 'Request failed.'
            );
        }

        return payload;
    }

    function buildOrderDetailsUpdateUrl(apiKey) {
        const base = getOrderAmendApiUrl();
        if (!base || !apiKey) return '';
        try {
            const url = new URL(base);
            url.searchParams.set('clientApiKey', apiKey);
            return url.toString();
        } catch {
            return '';
        }
    }

    async function postOrderShippingRefund() {
        const submitUrl = buildOrderDetailsUpdateUrl(getApiCredentials().clientApiKey);

        if (!submitUrl) {
            throw new Error('Order update API is not configured.');
        }
        const orderId = getOrderContext()?.orderSummary?.orderNo;

        const body = {
            action: 'shippingRefund',
            orderId,
            cancellationReasonId: ORDER_SHIPPING_REFUND_REASON_ID,
        };

        const res = await fetch(submitUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.errorMessage || data.message || `HTTP ${res.status}`);
        }
        return parseShopifyOrderStatusResponse(
            data,
            t().orderAmendsCancelOrderError || 'We could not complete this request.'
        );
    }

    async function postOrderCancel(reasonId) {
        const url = getOrderCancelApiUrl();
        if (!url) {
            throw new Error(
                t().orderAmendsCancelOrderNotConfigured || 'Order cancellation is not set up yet.'
            );
        }
        const refundSummary = computeOrderCancelRefundSummary();

        const body = buildOrderCancelPayload(reasonId, true, {
            items: refundSummary.items,
            refundTotal: refundSummary.refundTotal,
        });

        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.errorMessage || data.message || `HTTP ${res.status}`);
        }

        const result = parseShopifyOrderStatusResponse(
            data,
            t().orderAmendsCancelOrderError || 'We could not cancel the order.'
        );

        refetchOrderAfterCancel();
        return { result, refundSummary };
    }

    async function completeOrderCancelFromDrawer() {
        const reasonId = orderCancelDrawerState.selectedReason;
        if (!reasonId) {
            showOrderCancelGenericErrorPanel();
            return false;
        }
        if (!getOrderCancelApiUrl()) {
            global.alert(
                t().orderAmendsCancelOrderNotConfigured || 'Order cancellation is not set up yet.'
            );
            return false;
        }
        try {
            const { result, refundSummary } = await postOrderCancel(reasonId);
            showOrderCancelSuccessPanelForOutcome({ reasonId, result, refundSummary });
            return true;
        } catch (err) {
            handleOrderCancelApiFailure(err);
            return false;
        }
    }

    function showOrderCancelPriceSuccessPanel(refundAmount) {
        const tr = t();
        const cfg = getCancellationFlowConfig();
        const amount = formatMoney(refundAmount);
        const msg = replaceRefundDaysCopy(
            (tr.orderAmendsCancellationPriceSuccessMessageHtml || '').replace(/\{\{\s*amount\s*\}\}/g, amount),
            cfg
        );
        orderCancelDrawerState.cancellationResult = 'priceChangeSuccess';
        renderOrderCancelRetentionPanel({
            title: tr.orderAmendsCancellationPriceSuccessTitle || 'Retain',
            message: msg,
            buttons: [{ action: 'done-retain', label: tr.orderAmendsCancellationDone || 'Done', primary: true }],
        });
    }

    function showOrderCancelPriceFailurePanel() {
        const tr = t();
        renderOrderCancelRetentionPanel({
            title: tr.orderAmendsCancellationPriceFailureTitle || 'Failure',
            message: escapeHtml(tr.orderAmendsCancellationPriceFailureMessage || ''),
            buttons: [
                buildContactSupportButton(),
                { action: 'done-retain', label: tr.orderAmendsCancellationDone || 'Done', primary: true },
            ],
        });
    }

    function showOrderCancelShippingOfferPanel() {
        renderOrderCancelShippingRetentionStep();
    }

    function showOrderCancelShippingSuccessPanel(refundAmount) {
        const tr = t();
        const amount =
            refundAmount != null && Number.isFinite(parseFloat(refundAmount))
                ? parseFloat(refundAmount)
                : parseNumber(getOrderContext()?.orderSummary?.shippingTotal);
        const messageHtml = buildOrderCancelSuccessMessageHtml('shipping', { amount });
        orderCancelDrawerState.cancellationResult = 'shippingRefundAccepted';
        renderOrderCancelRetentionPanel({
            title: '',
            messageHtml,
            buttons: [{ action: 'done-retain', label: tr.orderAmendsCancellationDone || 'Done', primary: true }],
            centered: true,
        });
    }

    function showOrderCancelInProgressPanel() {
        const tr = t();
        orderCancelDrawerState.cancellationResult = 'inProgress';
        renderOrderCancelRetentionPanel({
            title: tr.orderAmendsCancellationInProgressTitle || 'Cancellation in progress',
            message: escapeHtml(tr.orderAmendsCancellationInProgressMessage || ''),
            buttons: [buildReturnsPolicyButton()],
        });
    }

    function showOrderCancelSuccessPanel(refundAmount) {
        const refundSummary = computeOrderCancelRefundSummary();
        const amount =
            refundAmount != null && Number.isFinite(parseFloat(refundAmount))
                ? parseFloat(refundAmount)
                : refundSummary.refundTotal;
        showOrderCancelSuccessPanelForOutcome({
            reasonId: orderCancelDrawerState.selectedReason,
            result: { refundAmount: amount },
            refundSummary,
        });
    }

    async function routeOrderCancelAfterReason(reasonId) {
        await routeCancellationAfterReason(createOrderCancellationCtx(), reasonId);
    }

    function closeOrderCancelDrawer() {
        getOrderTrackerDrawer()?.close();
        resetOrderCancelDrawerState();
    }

    function handleOrderCancelReasonNext() {
        const reasonId = orderCancelDrawerState.pendingReason || orderCancelDrawerState.selectedReason;
        if (!reasonId) {
            global.alert(t().orderAmendsCancelReasonRequired || 'Please select a reason before continuing.');
            return;
        }
        routeOrderCancelAfterReason(reasonId);
    }

    async function handleOrderCancelShippingRetentionNext() {
        const choice = orderCancelDrawerState.pendingShippingRetentionChoice;
        if (!choice) {
            global.alert(t().orderAmendsCancelReasonRequired || 'Please select an option before continuing.');
            return;
        }
        if (choice === 'accept-shipping-refund') {
            await runAcceptShippingRefund(createOrderCancellationCtx());
            return;
        }
        if (choice === 'continue-cancelling') {
            showOrderCancelLoadingPanel();
            await completeOrderCancelFromDrawer();
        }
    }

    async function handleOrderCancelDrawerNext() {
        if (orderCancelDrawerState.step === 'shipping-retention') {
            await handleOrderCancelShippingRetentionNext();
            return;
        }
        handleOrderCancelReasonNext();
    }

    async function handleOrderCancelDrawerAction(action) {
        switch (action) {
            case 'close':
                closeOrderCancelDrawer();
                break;
            case 'next':
                await handleOrderCancelDrawerNext();
                break;
            case 'back-reason':
                orderCancelDrawerState.pendingReason = null;
                orderCancelDrawerState.selectedReason = null;
                orderCancelDrawerState.pendingShippingRetentionChoice = null;
                renderOrderCancelReasonStep();
                break;
            case 'accept-shipping-refund':
                await runAcceptShippingRefund(createOrderCancellationCtx());
                break;
            case 'continue-cancelling':
                showOrderCancelLoadingPanel();
                await completeOrderCancelFromDrawer();
                break;
            case 'done-retain':
                closeOrderCancelDrawer();
                break;
            case 'done-success':
                resetOrderAmendsState();
                if (getOrderContext()) {
                    global.OrderAmendsFlow?.rebuildOrderAmendsRegistry?.(getOrderContext());
                }
                break;
            default:
                break;
        }
    }

    function handleOrderCancelDrawerDelegatedClick(ev) {
        if (!orderCancelFlowActive) return;
        const drawer = getOrderTrackerDrawer();
        if (!drawer?.classList.contains('order-tracker-drawer--open')) return;

        const actionBtn = ev.target.closest('[data-oa-order-cancel-action]');
        if (!actionBtn || !drawer.contains(actionBtn)) return;

        if (actionBtn.tagName === 'A' && actionBtn.href) return;

        ev.preventDefault();
        handleOrderCancelDrawerAction(actionBtn.dataset.oaOrderCancelAction);
    }

    function handleOrderCancelDrawerRadioChange(ev) {
        if (!orderCancelFlowActive) return;
        const input = ev.target;
        if (input?.type !== 'radio') return;

        const nextBtn = getOrderTrackerDrawer()?.querySelector('[data-oa-order-cancel-action="next"]');

        if (input.name === 'order-cancel-reason') {
            orderCancelDrawerState.pendingReason = input.value || null;
            if (nextBtn) nextBtn.disabled = !orderCancelDrawerState.pendingReason;
            return;
        }

        if (input.name === 'order-cancel-shipping-retention') {
            orderCancelDrawerState.pendingShippingRetentionChoice = input.value || null;
            if (nextBtn) nextBtn.disabled = !orderCancelDrawerState.pendingShippingRetentionChoice;
        }
    }

    async function startOrderCancelFlow() {
        if (transactionSummaryDrawerState.active) return;

        const orderData = getOrderContext();
        if (orderData && orderAmendsItemRegistry.size === 0) {
            rebuildOrderAmendsRegistry(orderData);
        }

        if (!getOrderCancelApiUrl()) {
            global.alert(
                t().orderAmendsCancelOrderNotConfigured ||
                    'Order cancellation is not set up yet. Please contact customer service.'
            );
            return;
        }

        resetOrderCancelDrawerState();

        orderCancelFlowActive = true;

        await runCancellationPreChecks({ scope: 'order' });

        renderOrderCancelReasonStep();
    }

    // --- Edit mode lifecycle ---

    function snapshotOrderAmendsBaselines() {
        orderAmendsBaselines.clear();
        orderAmendsItemRegistry.forEach((entry, lineKey) => {
            const orderedQuantity = Math.max(0, parseInt(entry.item.quantity, 10) || 0);
            orderAmendsBaselines.set(lineKey, {
                orderedQuantity,
                itemNumber: String(entry.item.itemNumber ?? ''),
            });
        });
    }

    function getLineStagedState(lineKey) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const baseline = orderAmendsBaselines.get(lineKey);
        const pending = orderAmendsPendingChanges.get(lineKey);
        const flow = getFlowState(lineKey);
        const container = getLineContainer(lineKey);

        const orderedQuantity =
            baseline?.orderedQuantity ??
            getOrderedQuantityForLine(lineKey);
        const itemNumber =
            baseline?.itemNumber ?? String(registryEntry?.item?.itemNumber ?? '');

        let requestedQuantity;
        if (typeof pending?.requestedQuantity === 'number') {
            requestedQuantity = pending.requestedQuantity;
        } else if (flow.isActive && typeof flow.targetQuantity === 'number') {
            requestedQuantity = flow.targetQuantity;
        } else {
            const parsedQty = parseInt(container?.querySelector('[data-oa-qty-current]')?.textContent, 10);
            requestedQuantity = Number.isFinite(parsedQty) ? parsedQty : orderedQuantity;
        }
        requestedQuantity = Math.max(0, requestedQuantity);

        const lineId =
            container?.dataset.oaLineDomId ||
            (registryEntry
                ? `oa-line-${registryEntry.bucket}-${registryEntry.batchIndex}-${registryEntry.itemIndex}`
                : '');

        return {
            lineKey,
            lineId,
            itemNumber,
            orderedQuantity,
            requestedQuantity,
            removalReason: pending?.removalReason ?? null,
            alternativeItemNumber: pending?.alternativeItemNumber ?? null,
            alternativeItemDescription: pending?.alternativeItemDescription ?? null,
            shipmentDocumentNo: String(registryEntry?.shipmentOrBatch?.documentNo ?? ''),
            shipmentStatus: registryEntry?.status ?? '',
        };
    }

    function lineHasSaveChanges(staged) {
        if (staged.requestedQuantity !== staged.orderedQuantity) return true;
        if (staged.alternativeItemNumber) return true;
        return false;
    }

    function getLineKeysInDomOrder() {
        const root = getRoot();
        if (!root) return [];
        return Array.from(root.querySelectorAll('[data-oa-line-key]'))
            .map((el) => el.dataset.oaLineKey)
            .filter(Boolean);
    }

    function isElementInViewport(el) {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        const viewHeight = window.innerHeight || document.documentElement.clientHeight;
        return rect.top < viewHeight && rect.bottom > 0;
    }

    function getLineValidationErrorBanner(lineKey) {
        const container = getLineContainer(lineKey);
        return container?.querySelector('[data-oa-validation-error]') ?? null;
    }

    function isLineValidationErrorVisible(lineKey) {
        const banner = getLineValidationErrorBanner(lineKey);
        if (!banner || banner.classList.contains('hidden')) return false;
        return isElementInViewport(banner);
    }

    function clearLineValidationError(lineKey) {
        const container = getLineContainer(lineKey);
        if (!container) return;
        const banner = getLineValidationErrorBanner(lineKey);
        setVisiblyHidden(banner, true);
        container.classList.remove('order-amends__item--validation-error');
    }

    function clearAllLineValidationErrors() {
        getLineKeysInDomOrder().forEach(clearLineValidationError);
    }

    function buildLineValidationErrorMessage() {
        const tr = t();
        const incomplete = tr.orderAmendsSaveChangesIncomplete || 'Changes incomplete';
        const detail = tr.orderAmendsSaveChangesMissingReason || 'please select a cancellation reason';
        return `${incomplete}: ${detail}`;
    }

    function showLineValidationError(lineKey, message) {
        const container = getLineContainer(lineKey);
        if (!container) return;
        const banner = getLineValidationErrorBanner(lineKey);
        const textEl = container.querySelector('[data-oa-validation-error-text]');
        if (textEl) textEl.textContent = message;
        setVisiblyHidden(banner, false);
        container.classList.add('order-amends__item--validation-error');
    }

    function lineHasMissingCancellationReason(lineKey) {
        const staged = getLineStagedState(lineKey);
        if (!lineHasSaveChanges(staged)) return false;
        const row = buildSaveAmendsItemRow(lineKey);
        if (!row) return false;
        return row.refundedQty > 0 && row.cancellationReasonId == null;
    }

    function validateOrderAmendsSaveBeforeSubmit() {
        clearAllLineValidationErrors();

        const invalidLineKeys = getLineKeysInDomOrder().filter(lineHasMissingCancellationReason);
        if (!invalidLineKeys.length) return true;

        const message = buildLineValidationErrorMessage();
        invalidLineKeys.forEach((lineKey) => showLineValidationError(lineKey, message));

        const lineKeyToScroll = invalidLineKeys.find((lineKey) => !isLineValidationErrorVisible(lineKey));
        const errorBanner = lineKeyToScroll ? getLineValidationErrorBanner(lineKeyToScroll) : null;

        if (errorBanner) {
            errorBanner.parentElement.scrollIntoView({
                behavior: 'smooth',
                block: window.innerWidth > 768 ? 'nearest' : 'start'
            });
        }

        return false;
    }

    function buildSaveAmendsItemRow(lineKey) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const staged = getLineStagedState(lineKey);
        const pending = orderAmendsPendingChanges.get(lineKey);
        if (!registryEntry) return null;

        const item = registryEntry.item || {};
        let refundedQty = pending?.refundedQty;
        let grossAmount = pending?.grossAmount;
        let lineId = pending?.lineKey != null ? String(pending.lineKey) : '';
        let cancellationReasonId = pending?.cancellationReasonId;

        if (
            refundedQty == null ||
            grossAmount == null ||
            !lineId ||
            cancellationReasonId == null
        ) {
            const computed = computeLineRemoveRefundFields({
                item,
                orderedQuantity: staged.orderedQuantity,
                requestedQuantity: staged.requestedQuantity,
                reasonId: pending?.removalReason ?? staged.removalReason,
            });
            if (refundedQty == null) refundedQty = computed.refundedQty;
            if (grossAmount == null) grossAmount = computed.grossAmount;
            if (!lineId) lineId = computed.apiLineKey;
            if (cancellationReasonId == null) cancellationReasonId = computed.cancellationReasonId;
        }

        if (refundedQty === 0) return null;

        if (!lineId) {
            console.warn('[Order amends] Missing API line id for', lineKey);
            return null;
        }

        const row = {
            lineId: toApiLineId(lineId),
            refundedQty,
            grossAmount,
        };

        if (cancellationReasonId != null) {
            row.cancellationReasonId = cancellationReasonId;
        }

        return row;
    }

    function buildAlternativeReplacementPayload(lineKey) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const pending = orderAmendsPendingChanges.get(lineKey);
        if (!registryEntry || !pending?.alternativeItemNumber) return null;

        const apiLineId = toApiLineId(String(registryEntry.item?.id ?? ''));
        if (!apiLineId) return null;

        const qty = Math.max(0, parseInt(pending.requestedQuantity, 10) || 0);
        const amount = roundMoneyAmount(parseNumber(pending.alternativeUnitPrice) * qty);

        if (!pending.alternativeLocationId) {
            console.warn('[Order amends] replaceProductInSalesOrder — alternative has no locationId, submitting empty string', lineKey);
        }

        return {
            action: 'replaceProductInSalesOrder',
            platformOrderNum: String(getOrderContext()?.orderSummary?.orderNo ?? ''),
            salesOrderId: String(getOrderContext()?.orderSummary?.orderInternalId ?? ''),
            lineId: apiLineId,
            replacementItem: {
                lineId: apiLineId,
                itemNumber: pending.alternativeItemNumber,
                quantity: qty,
                amount
            },
        };
    }

    function collectOrderAmendsSavePayload() {
        const items = [];
        orderAmendsItemRegistry.forEach((_entry, lineKey) => {
            const staged = getLineStagedState(lineKey);
            if (!lineHasSaveChanges(staged)) return;
            const row = buildSaveAmendsItemRow(lineKey);
            if (row) items.push(row);
        });
        const refundTotal = roundMoneyAmount(
            items.reduce((sum, row) => sum + parseNumber(row.grossAmount), 0)
        );
        return buildOrderCancelPayload(null, false, { items, refundTotal });
    }

    async function fetchAlternativeProductsForLine(lineKey) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const itemNumber = String(registryEntry?.item?.itemNumber ?? '');
        const currentQty = Math.max(0, parseInt(registryEntry?.item?.quantity, 10) || 0);
        const grossAmount = parseNumber(registryEntry?.item?.grossAmount);

        const submitUrl = buildOrderDetailsUpdateUrl(getApiCredentials().clientApiKey);
        if (!submitUrl || !itemNumber) {
            console.warn('[Order amends] getAlternativeProduct skipped — missing API URL or itemNumber', { submitUrl, itemNumber });
            return null;
        }

        try {
            const res = await fetch(submitUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    action: 'getAlternativeProducts',
                    currencyId: getStoreConfig().currencyId,
                    priceLevelId: getStoreConfig().priceLevelId,
                    items: [
                        {
                            itemNumber,
                            quantity: currentQty,
                            grossAmount: grossAmount.toFixed(2),
                        },
                    ],
                }),
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody.errorMessage || errBody.message || `HTTP ${res.status}`);
            }
            const data = await res.json().catch(() => ({}));
            return data;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    const ALTERNATIVES_CACHE_TTL_MS = 30 * 60 * 1000;

    function alternativesCacheKey(lineKey) {
        const itemNumber = orderAmendsItemRegistry.get(lineKey)?.item?.itemNumber ?? '';
        return `${lineKey}::${itemNumber}`;
    }

    async function fetchAlternativeProductAvailability(items) {
        if (!Array.isArray(items) || items.length === 0) return null;

        const submitUrl = buildOrderDetailsUpdateUrl(getApiCredentials().clientApiKey);
        if (!submitUrl) {
            console.warn('[Order amends] getAlternativeProductAvailability skipped — missing API URL');
            return null;
        }

        try {
            const res = await fetch(submitUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    action: 'getAvailability',
                    items,
                }),
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody.errorMessage || errBody.message || `HTTP ${res.status}`);
            }
            const data = await res.json().catch(() => ({}));
            return data;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    async function fetchAndFilterAlternativesForLine(lineKey) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const currentQty = Math.max(0, parseInt(registryEntry?.item?.quantity, 10) || 0);

        const data = await fetchAlternativeProductsForLine(lineKey);
        const apiAlternatives = data?.items?.[0]?.alternatives;
        if (!data || !Array.isArray(apiAlternatives)) return null;
        if (apiAlternatives.length === 0) return [];

        const { grossAmount: originalGrossAmount } = getOriginalUnitPriceForLine(lineKey);

        const cards = apiAlternatives
            .map((alt) => ({
                itemNumber: String(alt.item_number ?? ''),
                productId: String(alt.product_id ?? ''),
                title: String(alt.product_name ?? ''),
                imageUrl: sanitizeImageUrl(alt.item_image) || sanitizeImageUrl(global.placeholderImage) || '',
                quantity: currentQty,
                price: parseNumber(alt.price),
            }))
            .filter((card) => roundMoneyAmount(card.price * card.quantity) <= roundMoneyAmount(originalGrossAmount));

        if (cards.length === 0) return [];

        const availabilityData = await fetchAlternativeProductAvailability(
            cards.map((card) => ({ itemNumber: card.itemNumber, productId: card.productId, quantity: card.quantity }))
        );
        const availabilityItems = availabilityData?.data?.items ?? availabilityData?.items;
        if (!Array.isArray(availabilityItems)) return null; // whole availability call failed/malformed — not cacheable, distinct from a call that validly returned nothing

        const estimateByItemNumber = new Map(
            availabilityItems
                .filter((item) => item && item.itemNumber != null)
                .map((item) => [String(item.itemNumber), item.availabilityEstimate])
        );

        return cards.reduce((resolved, card) => {
            const estimate = estimateByItemNumber.get(card.itemNumber);
            const label = String(estimate?.label ?? '').trim();
            if (!estimate || (!label && !estimate.inStockNow)) return resolved; // no availability estimate — drop it

            card.availabilityStatus = 'resolved';
            card.deliveryDateText = label;
            card.deliveryDate = estimate.availabilityDate || '';
            card.inStockNow = Boolean(estimate.inStockNow);
            card.locationId = String(estimate.locationId ?? '').replace(/^loc_/, '');
            resolved.push(card);
            return resolved;
        }, []);
    }

    function getAlternativesForLine(lineKey) {
        const cacheKey = alternativesCacheKey(lineKey);
        const cached = alternativesProductsCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < ALTERNATIVES_CACHE_TTL_MS) return cached.promise;

        const promise = fetchAndFilterAlternativesForLine(lineKey).then((cards) => {
            if (!Array.isArray(cards)) {
                alternativesProductsCache.delete(cacheKey);
                return [];
            }
            return cards;
        });

        alternativesProductsCache.set(cacheKey, { promise, timestamp: Date.now() });
        return promise;
    }

    function formatAlternativeDeliveryDate(isoString) {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (Number.isNaN(date.getTime())) return '';

        const monthNamesShort = global.orderTrackerTranslations?.monthNamesShort;
        if (!Array.isArray(monthNamesShort) || !monthNamesShort[date.getUTCMonth()]) return '';

        const day = date.getUTCDate();
        const month = monthNamesShort[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        const currentYear = new Date().getUTCFullYear();

        return year === currentYear ? `${day} ${month}` : `${day} ${month} ${year}`;
    }

    function alternativeAvailabilityMeta(alt, tr) {
        if (alt.inStockNow) {
            return {
                className: 'order-amends__alternative-pick-meta order-amends__alternative-pick-meta--in-stock',
                text: tr.orderAmendsAlternativesInStock || 'In stock',
            };
        }
        const formattedDate = formatAlternativeDeliveryDate(alt.deliveryDate);
        return {
            className: 'order-amends__alternative-pick-meta',
            text: (tr.orderAmendsAlternativesDeliveryLabel || 'Estimated to get it by {{ date }}').replace(/\{\{\s*date\s*\}\}/g, formattedDate || alt.deliveryDateText || ''),
        };
    }

    function resetAlternativesDrawerState() {
        alternativesDrawerState = {
            active: false,
            lineKey: null,
            alternatives: [],
            selectedIndex: null,
        };
    }

    function getOriginalUnitPriceForLine(lineKey) {
        const item = orderAmendsItemRegistry.get(lineKey)?.item;
        const qty = Math.max(0, parseInt(item?.quantity, 10) || 0);
        const grossAmount = parseNumber(item?.grossAmount);
        const originalUnitPrice = qty > 0 ? grossAmount / qty : 0;
        return { qty, grossAmount, originalUnitPrice };
    }

    function computePriceDiff(altPrice, originalUnitPrice, qty) {
        const tr = t();
        const altTotal = roundMoneyAmount(altPrice * qty);
        const originalTotal = roundMoneyAmount(originalUnitPrice * qty);
        const diff = roundMoneyAmount(originalTotal - altTotal);

        if (Math.abs(diff) < 0.01) {
            const label = tr.orderAmendsAlternativesNoPriceDifference || 'No price difference';
            return { label, prefixText: label, amountText: '', amount: 0, type: 'none' };
        }

        const isRefund = diff > 0;
        const template = isRefund
            ? (tr.orderAmendsAlternativesDifferenceRefunded || 'Difference to be refunded {{ amount }}')
            : (tr.orderAmendsAlternativesExtraCost || 'Extra cost {{ amount }}');
        const amount = isRefund ? diff : Math.abs(diff);
        const amountText = `${isRefund ? '−' : '+'} ${formatMoney(amount)}`;

        return {
            label: template.replace(/\{\{\s*amount\s*\}\}/g, amountText),
            prefixText: template.replace(/\{\{\s*amount\s*\}\}/g, '').trim(),
            amountText,
            amount: isRefund ? diff : -amount,
            type: isRefund ? 'refund' : 'extra',
        };
    }

    function renderAlternativeCardHtml(alt, index, originalUnitPrice) {
        const tr = t();
        const qty = alt.quantity || 0;
        const priceDiff = computePriceDiff(alt.price, originalUnitPrice, qty);
        const meta = alternativeAvailabilityMeta(alt, tr);
        const metaHtml = `<p class="${meta.className}">${escapeHtml(meta.text)}</p>`;
        const qtyTemplate = tr.orderAmendsQuantityLabelTemplate || 'Quantity: {{ quantity }}';
        const qtyHtml = `<p class="order-amends__alternative-pick-qty">${escapeHtml(
            qtyTemplate.replace(/\{\{\s*quantity\s*\}\}/g, String(qty))
        )}</p>`;
        const imgHtml = alt.imageUrl
            ? `<img src="${escapeHtmlAttr(alt.imageUrl)}" alt="" class="order-amends__alternative-img" width="80" height="80">`
            : `<div class="order-amends__alternative-img order-amends__alternative-img--placeholder" aria-hidden="true"></div>`;
        const priceDiffClass = priceDiff.type === 'none'
            ? 'order-amends__alternative-pick-diff'
            : priceDiff.type === 'refund'
            ? 'order-amends__alternative-pick-diff order-amends__alternative-pick-diff--refund'
            : 'order-amends__alternative-pick-diff order-amends__alternative-pick-diff--extra';

        const priceDiffHtml = priceDiff.type === 'none'
            ? escapeHtml(priceDiff.label)
            : `${escapeHtml(priceDiff.prefixText)} <span class="order-amends__alternative-pick-diff-amount">${escapeHtml(priceDiff.amountText)}</span>`;

        return `
            <button type="button"
                class="order-amends__alternative-pick"
                data-oa-alt-index="${escapeHtmlAttr(String(index))}"
                aria-pressed="false">
                ${imgHtml}
                <div class="order-amends__alternative-pick-body">
                    <div class="order-amends__alternative-pick-top">
                        <p class="order-amends__alternative-pick-title">${escapeHtml(alt.title)}</p>
                        ${metaHtml}
                    </div>
                    ${qtyHtml}
                    <p class="${priceDiffClass}">${priceDiffHtml}</p>
                </div>
            </button>`;
    }

    function renderAlternativesDrawerHtml(lineKey, alternatives) {
        const tr = t();
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const productName = escapeHtml(String(registryEntry?.item?.itemDescription ?? ''));
        const description = (tr.orderAmendsAlternativesDrawerDescription || 'Change the {{ product }} for an item listed below.')
            .replace(/\{\{\s*product\s*\}\}/g, `<strong>${productName}</strong>`);

        const { originalUnitPrice } = getOriginalUnitPriceForLine(lineKey);

        const cardsHtml = alternatives.length
            ? alternatives.map((alt, i) => renderAlternativeCardHtml(alt, i, originalUnitPrice)).join('')
            : `<p class="order-amends__no-alternatives">${escapeHtml(tr.orderAmendsNoAlternatives || 'No alternative products are listed for this item.')}</p>`;

        const hasSelection = alternativesDrawerState.selectedIndex !== null;

        return `
            <div class="order-amends__alternatives-drawer-root">
                <div class="order-tracker-drawer__sub-header">
                    <p>${description}</p>
                </div>
                <div class="order-amends__alternatives-scroll">
                    <div class="order-amends__alternatives-list">${cardsHtml}</div>
                </div>
                <div class="order-details__cancel-reason-footer button-group">
                    ${renderDrawerButton(
                        { action: 'next', label: tr.orderAmendsRemoveReasonNext || 'Next', primary: true, disabled: !hasSelection },
                        { variant: 'outline', actionAttr: 'data-oa-alternatives-action' }
                    )}
                    ${renderDrawerButton(
                        { action: 'back', label: tr.orderAmendsCancellationBack || 'Back' },
                        { variant: 'outline', actionAttr: 'data-oa-alternatives-action' }
                    )}
                </div>
            </div>`;
    }

    async function openAlternativesDrawer(lineKey) {
        if (orderCancelFlowActive) return false;
        if (transactionSummaryDrawerState.active) return false;

        const drawer = getOrderTrackerDrawer();
        if (!drawer) return false;

        alternativesDrawerState.active = true;
        alternativesDrawerState.lineKey = lineKey;
        alternativesDrawerState.alternatives = [];
        alternativesDrawerState.selectedIndex = null;

        const tr = t();
        const drawerTitle = tr.orderAmendsAlternativesDrawerTitle || 'Select an alternative';

        drawer.open({
            title: drawerTitle,
            html: orderCancelRetentionBodyHtml({
                title: '',
                messageHtml: cancellationLoadingMessageHtml(),
                innerButtons: [],
                centered: true,
            }),
        });

        const alternatives = await getAlternativesForLine(lineKey);

        // Drawer may have been closed, or superseded by another line, while the fetch was in flight.
        if (!alternativesDrawerState.active || alternativesDrawerState.lineKey !== lineKey) return true;

        alternativesDrawerState.alternatives = alternatives;

        drawer.open({
            title: drawerTitle,
            html: renderAlternativesDrawerHtml(lineKey, alternatives),
        });

        return true;
    }

    function handleAlternativeCardSelect(index) {
        const drawer = getOrderTrackerDrawer();
        if (!drawer) return;

        alternativesDrawerState.selectedIndex = index;

        const cards = drawer.querySelectorAll('[data-oa-alt-index]');
        cards.forEach((card) => {
            const cardIndex = parseInt(card.dataset.oaAltIndex, 10);
            const isSelected = cardIndex === index;
            card.classList.toggle('order-amends__alternative-pick--selected', isSelected);
            card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        const nextBtn = drawer.querySelector('[data-oa-alternatives-action="next"]');
        if (nextBtn) nextBtn.disabled = false;
    }

    function handleAlternativesNext() {
        const { lineKey, alternatives, selectedIndex } = alternativesDrawerState;
        if (lineKey == null || selectedIndex == null) return;
        const alt = alternatives[selectedIndex];
        if (!alt) return;

        if (!stageAlternativeSelection(lineKey, alt)) return;
        updateSaveChangesButtonState();
        getOrderTrackerDrawer()?.close();
        showAlternativeStagedStep(lineKey, alt);
    }

    function stageAlternativeSelection(lineKey, alt) {
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        if (!registryEntry) {
            console.warn('[Order amends] stageAlternativeSelection skipped — missing registry entry for line', lineKey);
            return false;
        }
        const existing = orderAmendsPendingChanges.get(lineKey) || {};
        const { qty: orderedQty, originalUnitPrice } = getOriginalUnitPriceForLine(lineKey);
        const priceDiff = computePriceDiff(alt.price, originalUnitPrice, orderedQty);

        orderAmendsPendingChanges.set(lineKey, {
            ...existing,
            alternativeItemNumber: alt.itemNumber,
            alternativeItemDescription: alt.title,
            alternativeImageUrl: alt.imageUrl || '',
            alternativeUnitPrice: parseNumber(alt.price),
            alternativeLocationId: alt.locationId || '',
            requestedQuantity: orderedQty,
            cancelEntireLine: false,
            refundedQty: 0,
            grossAmount: priceDiff.type === 'refund' ? priceDiff.amount : 0,
        });
        return true;
    }

    function showAlternativeStagedStep(lineKey, alt) {
        const container = getLineContainer(lineKey);
        if (!container) return;

        const tr = t();
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        const { qty, originalUnitPrice } = getOriginalUnitPriceForLine(lineKey);
        const priceDiff = computePriceDiff(alt.price, originalUnitPrice, qty);
        const altTotal = roundMoneyAmount(alt.price * qty);

        const originalProductLabel = tr.orderAmendsAlternativesOriginalProduct || 'Original product:';
        const originalDesc = escapeHtml(String(registryEntry?.item?.itemDescription ?? ''));
        const altStatusLabel = tr.orderAmendsTransactionSummaryAlternativeSelected || '(Alternative selected)';

        const imgContainer = container.querySelector('.shipment__item-image-container');
        if (imgContainer) {
            const altSrc = sanitizeImageUrl(alt.imageUrl || '');
            imgContainer.innerHTML = altSrc
                ? `<img src="${escapeHtmlAttr(altSrc)}" alt="" class="shipment__item-image">`
                : `<div class="shipment__item-placeholder shipment__item-image"></div>`;
        }

        const nameEl = container.querySelector('.shipment__item-name');
        if (nameEl) {
            nameEl.innerHTML = `<span data-oa-alt-status class="order-transaction-summary__status order-transaction-summary__status--alternative">${escapeHtml(altStatusLabel)}</span> ${escapeHtml(alt.title)}`;
        }

        const priceEl = container.querySelector('[data-oa-display-price]');
        if (priceEl) priceEl.textContent = formatMoney(altTotal);

        const amountEl = container.querySelector('.shipment__item-amount');
        if (amountEl && priceDiff.type !== 'none' && !container.querySelector('[data-oa-alt-diff]')) {
            const diffEl = document.createElement('p');
            diffEl.setAttribute('data-oa-alt-diff', '');
            diffEl.className = `order-amends__alternative-pick-diff${priceDiff.type === 'refund' ? ' order-amends__alternative-pick-diff--refund' : ' order-amends__alternative-pick-diff--extra'}`;
            diffEl.textContent = priceDiff.label;
            amountEl.insertAdjacentElement('afterend', diffEl);
        }

        const summaryTextEl = container.querySelector('[data-oa-summary-text]');
        if (summaryTextEl) {
            summaryTextEl.innerHTML = `<strong>${escapeHtml(originalProductLabel)}</strong> ${originalDesc} x${escapeHtml(String(qty))}`;
        }

        setVisiblyHidden(container.querySelector('.shipment__item-product-actions'), true);
        setVisiblyHidden(container.querySelector('[data-oa-summary-step]'), false);
        setVisiblyHidden(container.querySelector('[data-oa-validation-error]'), true);

        clearLineValidationError(lineKey);
    }

    function clearAlternativeSelection(lineKey) {
        const container = getLineContainer(lineKey);
        if (!container) return;

        const pending = orderAmendsPendingChanges.get(lineKey);
        if (pending) {
            const updated = { ...pending };
            delete updated.alternativeItemNumber;
            delete updated.alternativeItemDescription;
            delete updated.alternativeImageUrl;
            delete updated.alternativeUnitPrice;
            delete updated.refundedQty;
            delete updated.grossAmount;
            if (updated.requestedQuantity === (orderAmendsBaselines.get(lineKey)?.orderedQuantity ?? updated.requestedQuantity)) {
                orderAmendsPendingChanges.delete(lineKey);
            } else {
                orderAmendsPendingChanges.set(lineKey, updated);
            }
        }

        // Remove injected alt status/diff elements
        container.querySelector('[data-oa-alt-status]')?.remove();
        container.querySelector('[data-oa-alt-diff]')?.remove();

        // Restore original product image and title from registry
        const registryEntry = orderAmendsItemRegistry.get(lineKey);
        if (registryEntry) {
            const item = registryEntry.item;
            const imgContainer = container.querySelector('.shipment__item-image-container');
            if (imgContainer) {
                const origSrc = sanitizeImageUrl(item.itemImageUrl || '');
                imgContainer.innerHTML = origSrc
                    ? `<img src="${escapeHtmlAttr(origSrc)}" alt="${escapeHtmlAttr(item.itemDescription || '')}" class="shipment__item-image">`
                    : `<div class="shipment__item-placeholder shipment__item-image"></div>`;
            }
            const nameEl = container.querySelector('.shipment__item-name');
            if (nameEl) nameEl.textContent = item.itemDescription || '';
        }

        setVisiblyHidden(container.querySelector('[data-oa-summary-step]'), true);
        const summaryTextEl = container.querySelector('[data-oa-summary-text]');
        if (summaryTextEl) summaryTextEl.innerHTML = '';

        setVisiblyHidden(container.querySelector('.shipment__item-product-actions'), false);
        resetQtyAmendStateUi(lineKey);

        updateLineDisplay(lineKey);
    }

    function handleAlternativesDrawerAction(action) {
        if (action === 'next') {
            handleAlternativesNext();
        } else if (action === 'back') {
            getOrderTrackerDrawer()?.close();
        }
    }

    function handleAlternativesDrawerDelegatedClick(ev) {
        if (!alternativesDrawerState.active) return;

        const actionBtn = ev.target.closest('[data-oa-alternatives-action]');
        if (actionBtn) {
            ev.preventDefault();
            handleAlternativesDrawerAction(actionBtn.dataset.oaAlternativesAction);
            return;
        }

        const card = ev.target.closest('[data-oa-alt-index]');
        if (card) {
            ev.preventDefault();
            const index = parseInt(card.dataset.oaAltIndex, 10);
            if (!isNaN(index)) handleAlternativeCardSelect(index);
        }
    }

    // --- Transaction summary drawer (Save changes → Confirm POST) ---

    function resetTransactionSummaryDrawerState() {
        transactionSummaryDrawerState = {
            active: false,
            step: 'summary',
            payload: null,
            submitUrl: null,
            model: null,
        };
    }

    function classifyAmendRow(staged) {
        if (staged.alternativeItemNumber && staged.requestedQuantity > 0) return 'alternativeSelected';
        if (staged.requestedQuantity === 0) return 'cancelledProduct';
        if (staged.requestedQuantity < staged.orderedQuantity) return 'quantityReduction';
        return null;
    }

    function sanitizeImageUrl(url) {
        const s = (url || '').trim();
        const lower = s.toLowerCase();
        return lower.startsWith('https://') || lower.startsWith('//') ? s : '';
    }

    function buildTransactionSummaryModel(payload) {
        const tr = t();
        const rows = [];
        let cancellationTotal = 0;
        let productDifferenceTotal = 0;
        let quantityReductionTotal = 0;

        orderAmendsItemRegistry.forEach((registryEntry, lineKey) => {
            const staged = getLineStagedState(lineKey);
            if (!lineHasSaveChanges(staged)) return;

            const type = classifyAmendRow(staged);
            if (!type) return;

            const item = registryEntry.item || {};
            const pending = orderAmendsPendingChanges.get(lineKey);
            let refundedQty = pending?.refundedQty;
            let grossAmount = pending?.grossAmount;

            if (refundedQty == null || grossAmount == null) {
                const computed = computeLineRemoveRefundFields({
                    item,
                    orderedQuantity: staged.orderedQuantity,
                    requestedQuantity: staged.requestedQuantity,
                    reasonId: pending?.removalReason ?? staged.removalReason,
                });
                if (refundedQty == null) refundedQty = computed.refundedQty;
                if (grossAmount == null) grossAmount = computed.grossAmount;
            }

            const qtyTemplate =
                tr.orderAmendsTransactionSummaryQuantityLabel || 'Quantity: {{ qty }}';
            const signedQty =
                type === 'cancelledProduct'
                    ? -refundedQty
                    : type === 'alternativeSelected'
                        ? staged.requestedQuantity
                        : refundedQty;

            const row = {
                type,
                lineKey,
                imageUrl:
                    sanitizeImageUrl(item.itemImageUrl) ||
                    sanitizeImageUrl(global.placeholderImage) ||
                    '',
                title: String(item.itemDescription ?? ''),
                quantityLabel: qtyTemplate.replace(/\{\{\s*qty\s*\}\}/g, String(signedQty)),
                remainingQty: staged.requestedQuantity,
                removedQty: refundedQty,
                amount: grossAmount,
                amountDisplay: grossAmount > 0 ? '−' + formatMoney(grossAmount) : formatMoney(0),
            };

            if (type === 'cancelledProduct') {
                cancellationTotal += grossAmount;
                rows.push(row);
            } else if (type === 'alternativeSelected') {
                const altRefund = pending?.grossAmount ?? 0;
                const { qty, originalUnitPrice } = getOriginalUnitPriceForLine(lineKey);
                const altPrice = pending?.alternativeUnitPrice ?? originalUnitPrice;
                const diff = computePriceDiff(altPrice, originalUnitPrice, qty);
                if (diff.type === 'refund') {
                    row.amountDisplay = diff.amountText;
                    row.amountLabel = diff.prefixText;
                } else {
                    row.amountDisplay = diff.label;
                }
                productDifferenceTotal += altRefund;
                rows.push(row);
            } else if (type === 'quantityReduction') {
                quantityReductionTotal += grossAmount;
                rows.push(row);
            }
        });

        const totalRefund =
            (payload?.refundTotal ?? cancellationTotal + quantityReductionTotal) + productDifferenceTotal;

        return {
            rows,
            totals: {
                cancellationTotal: roundMoneyAmount(cancellationTotal),
                productDifference: roundMoneyAmount(productDifferenceTotal),
                quantityReduction: roundMoneyAmount(quantityReductionTotal),
                totalRefund: roundMoneyAmount(totalRefund),
            },
        };
    }

    function transactionSummaryFooterHtml(buttons) {
        return `<div class="order-details__cancel-reason-footer button-group order-transaction-summary__footer">${(buttons || [])
            .map((b) => renderDrawerButton(b, { variant: 'outline', actionAttr: 'data-oa-transaction-summary-action' }))
            .join('')}</div>`;
    }

    function renderTransactionSummaryRowHtml(row) {
        const tr = t();
        const buildImgHtml = (src) => src
            ? `<img src="${escapeHtmlAttr(src)}" alt="" class="order-transaction-summary__image" width="80" height="80">`
            : `<div class="order-transaction-summary__image order-transaction-summary__image--placeholder" aria-hidden="true"></div>`;
        const imgHtml = buildImgHtml(row.imageUrl);
        const failedClass = row.failed ? ' order-transaction-summary__item--failed' : '';

        if (row.type === 'cancelledProduct') {
            const statusLabel =
                tr.orderAmendsTransactionSummaryCancelledProduct || '(Cancelled Product)';

            return `
                <article class="order-transaction-summary__item order-transaction-summary__item--cancelled${failedClass}">
                    ${imgHtml}
                    <div class="order-transaction-summary__item-body">
                        <p class="order-transaction-summary__status order-transaction-summary__status--cancelled">${escapeHtml(statusLabel)}</p>
                        <p class="order-transaction-summary__title">${escapeHtml(row.title)}</p>
                        <p class="order-transaction-summary__qty">${escapeHtml(row.quantityLabel)}</p>
                        <p class="order-transaction-summary__amount">${escapeHtml(row.amountDisplay)}</p>
                    </div>
                </article>`;
        }

        if (row.type === 'alternativeSelected') {
            const statusLabel = tr.orderAmendsTransactionSummaryAlternativeSelected || '(Alternative selected)';
            const pending = orderAmendsPendingChanges.get(row.lineKey);
            const altTitle = escapeHtml(String(pending?.alternativeItemDescription ?? row.title));
            const altImgHtml = buildImgHtml(pending?.alternativeImageUrl || row.imageUrl);
            const priceDiffHtml = row.amountDisplay
                ? `<p class="order-transaction-summary__amount">${escapeHtml(row.amountDisplay)}${row.amountLabel ? ` <span class="order-transaction-summary__amount-label">${escapeHtml(row.amountLabel)}</span>` : ''}</p>`
                : '';

            return `
                <article class="order-transaction-summary__item order-transaction-summary__item--alternative${failedClass}">
                    ${altImgHtml}
                    <div class="order-transaction-summary__item-body">
                        <p class="order-transaction-summary__status order-transaction-summary__status--alternative">${escapeHtml(statusLabel)}</p>
                        <p class="order-transaction-summary__title">${altTitle}</p>
                        <p class="order-transaction-summary__qty">${escapeHtml(row.quantityLabel)}</p>
                        ${priceDiffHtml}
                    </div>
                </article>`;
        }

        if (row.type === 'quantityReduction') {
            const remainingTemplate =
                tr.orderAmendsTransactionSummaryQtyRemaining || 'Quantity remaining: {{ qty }}';
            const removingTemplate =
                tr.orderAmendsTransactionSummaryQtyRemoving || 'Quantity removing: {{ qty }}';
            const remainingLabel = remainingTemplate.replace(
                /\{\{\s*qty\s*\}\}/g,
                String(row.remainingQty)
            );
            const removingLabel = removingTemplate.replace(
                /\{\{\s*qty\s*\}\}/g,
                '-' + String(row.removedQty)
            );

            return `
                <article class="order-transaction-summary__item order-transaction-summary__item--reduced${failedClass}">
                    ${imgHtml}
                    <div class="order-transaction-summary__item-body">
                        <p class="order-transaction-summary__title">${escapeHtml(row.title)}</p>
                        <p class="order-transaction-summary__qty order-transaction-summary__qty--remaining">${escapeHtml(remainingLabel)}</p>
                        <p class="order-transaction-summary__qty order-transaction-summary__qty--removing">${escapeHtml(removingLabel)}</p>
                        <p class="order-transaction-summary__amount">${escapeHtml(row.amountDisplay)}</p>
                    </div>
                </article>`;
        }

        return '';
    }

    function renderTransactionSummaryTotalsHtml(totals) {
        const tr = t();
        const row = (label, amount) =>
            `<div class="order-transaction-summary__total-row">
                <span class="order-transaction-summary__total-label">${escapeHtml(label)}</span>
                <span class="order-transaction-summary__total-value">${escapeHtml(formatMoney(amount))}</span>
            </div>`;

        return `
            <div class="order-transaction-summary__totals">
                ${row(
                    tr.orderAmendsTransactionSummaryCancellationTotal || 'Cancellation Total',
                    totals.cancellationTotal
                )}
                ${row(
                    tr.orderAmendsTransactionSummaryProductDifference || 'Product difference',
                    totals.productDifference
                )}
                ${row(
                    tr.orderAmendsTransactionSummaryQuantityReduction || 'Quantity Reduction',
                    totals.quantityReduction
                )}
                <div class="order-transaction-summary__total-divider"></div>
                <div class="order-transaction-summary__total-row order-transaction-summary__total-row--final">
                    <span class="order-transaction-summary__total-label">${escapeHtml(
                        tr.orderAmendsTransactionSummaryTotalRefund || 'Total refund'
                    )}</span>
                    <span class="order-transaction-summary__total-value">${escapeHtml(
                        formatMoney(totals.totalRefund)
                    )}</span>
                </div>
            </div>`;
    }

    function renderTransactionSummaryStepHtml(model) {
        const tr = t();
        const visibleRows = model.rows.filter(
            (row) => row.type === 'cancelledProduct' || row.type === 'quantityReduction' || row.type === 'alternativeSelected'
        );
        const itemsHtml = visibleRows.map(renderTransactionSummaryRowHtml).join('');

        return `
            <div class="order-transaction-summary-drawer-root">
                <div class="order-transaction-summary__scroll">
                    <div class="order-transaction-summary__items">${itemsHtml}</div>
                    ${renderTransactionSummaryTotalsHtml(model.totals)}
                </div>
                ${transactionSummaryFooterHtml([
                    {
                        action: 'confirm',
                        label: tr.orderAmendsTransactionSummaryConfirm || 'Confirm',
                        primary: true,
                    },
                    {
                        action: 'back',
                        label: tr.orderAmendsTransactionSummaryBack || 'Back',
                    },
                ])}
            </div>`;
    }

    function openTransactionSummaryDrawerContent(title, rootHtml) {
        const drawer = getOrderTrackerDrawer();
        if (!drawer) return false;
        drawer.open({
            title: title || t().orderAmendsTransactionSummaryTitle || 'Transaction Summary',
            html: rootHtml,
        });
        return true;
    }

    function renderTransactionSummaryTerminalPanel({ messageHtml, footerButtons }) {
        const tr = t();
        const body = orderCancelRetentionBodyHtml({
            title: '',
            messageHtml,
            innerButtons: [],
            centered: true,
        });
        const buttons =
            footerButtons ??
            [
                {
                    action: 'done',
                    label: tr.orderAmendsCancellationDone || 'Done',
                    primary: true,
                },
            ];
        const rootHtml = `<div class="order-transaction-summary-drawer-root order-transaction-summary-drawer-root--terminal">${body}${transactionSummaryFooterHtml(buttons)}</div>`;
        openTransactionSummaryDrawerContent(tr.orderAmendsTransactionSummaryTitle, rootHtml);
    }

    function showTransactionSummaryLoadingPanel() {
        transactionSummaryDrawerState.step = 'loading';
        const tr = t();
        const rootHtml = `
            <div class="order-transaction-summary-drawer-root order-transaction-summary-drawer-root--terminal">
                ${orderCancelRetentionBodyHtml({
                    title: '',
                    messageHtml: cancellationLoadingMessageHtml(),
                    innerButtons: [],
                    centered: true,
                })}
            </div>`;
        openTransactionSummaryDrawerContent(tr.orderAmendsTransactionSummaryTitle, rootHtml);
    }

    function showTransactionSummarySuccessPanel() {
        transactionSummaryDrawerState.step = 'success';
        const tr = t();
        const messageHtml = `<p>${escapeHtml(
            tr.orderAmendsSaveChangesSuccess || 'Your changes have been saved.'
        )}</p>`;
        renderTransactionSummaryTerminalPanel({ messageHtml });
    }

    function showTransactionSummaryErrorPanel() {
        transactionSummaryDrawerState.step = 'error';
        const tr = t();
        const messageHtml = `<p>${escapeHtml(
            tr.orderAmendsSaveChangesError ||
                'We could not save your changes. Please try again or contact customer service.'
        )}</p>`;
        renderTransactionSummaryTerminalPanel({
            messageHtml,
            footerButtons: [
                {
                    action: 'back',
                    label: tr.orderAmendsTransactionSummaryBack || 'Back',
                    primary: true,
                },
            ],
        });
    }

    function showTransactionSummaryPartialFailurePanel(resultsByLineKey) {
        transactionSummaryDrawerState.step = 'partialFailure';
        const tr = t();
        const model = transactionSummaryDrawerState.model;
        const visibleRows = (model?.rows || []).filter(
            (row) => row.type === 'cancelledProduct' || row.type === 'quantityReduction' || row.type === 'alternativeSelected'
        );

        const itemsHtml = visibleRows
            .map((row) => renderTransactionSummaryRowHtml({
                ...row,
                failed: resultsByLineKey.get(row.lineKey) === false,
            }))
            .join('');

        const messageHtml = `<p class="order-transaction-summary__partial-failure-message">${escapeHtml(
            tr.orderAmendsTransactionSummaryPartialFailureMessage ||
                'One or more request could not be completed at this time'
        )}</p>`;

        const noticeHtml = `<div class="order-transaction-summary__partial-failure-notice"><p>${escapeHtml(
            tr.orderAmendsTransactionSummaryPartialFailureNotice ||
                'One of your requests was unsuccessful. Please try again or contact our Support Team for further assistance.'
        )}</p></div>`;

        const rootHtml = `
            <div class="order-transaction-summary-drawer-root">
                ${messageHtml}
                ${noticeHtml}
                <div class="order-transaction-summary__scroll">
                    <div class="order-transaction-summary__items">${itemsHtml}</div>
                </div>
                ${transactionSummaryFooterHtml([
                    buildContactSupportButton({ primary: true }),
                    {
                        action: 'close',
                        label: tr.orderAmendsTransactionSummaryClose || 'Close',
                    },
                ])}
            </div>`;

        openTransactionSummaryDrawerContent(tr.orderAmendsTransactionSummaryTitle, rootHtml);
    }

    function openTransactionSummaryDrawer({ payload, submitUrl }) {
        if (orderCancelFlowActive) return false;
        if (alternativesDrawerState.active) return false;

        const model = buildTransactionSummaryModel(payload);
        const visibleRows = model.rows.filter(
            (row) => row.type === 'cancelledProduct' || row.type === 'quantityReduction' || row.type === 'alternativeSelected'
        );
        if (!visibleRows.length) return false;

        resetTransactionSummaryDrawerState();
        transactionSummaryDrawerState.active = true;
        transactionSummaryDrawerState.step = 'summary';
        transactionSummaryDrawerState.payload = payload;
        transactionSummaryDrawerState.submitUrl = submitUrl;
        transactionSummaryDrawerState.model = model;

        return openTransactionSummaryDrawerContent(
            t().orderAmendsTransactionSummaryTitle,
            renderTransactionSummaryStepHtml(model)
        );
    }

    function closeTransactionSummaryDrawer() {
        getOrderTrackerDrawer()?.close();
        resetTransactionSummaryDrawerState();
    }

    async function commitOrderAmendsSave() {
        const { payload, submitUrl, model } = transactionSummaryDrawerState;
        if (!submitUrl) {
            throw new Error('Nothing to save.');
        }

        const resultsByLineKey = new Map();
        const rows = model?.rows || [];
        const batchLineKeys = rows
            .filter((row) => row.type === 'cancelledProduct' || row.type === 'quantityReduction')
            .map((row) => row.lineKey);
        const alternativeRows = rows.filter((row) => row.type === 'alternativeSelected');

        if (payload?.items?.length) {
            let batchSucceeded = true;
            try {
                await postOrderAmendsSave(submitUrl, payload);
            } catch (err) {
                console.error(err);
                batchSucceeded = false;
            }
            batchLineKeys.forEach((lineKey) => resultsByLineKey.set(lineKey, batchSucceeded));
        }

        if (alternativeRows.length) {
            for (const row of alternativeRows) {
                try {
                    const altPayload = buildAlternativeReplacementPayload(row.lineKey);
                    if (!altPayload) throw new Error('Missing replacement payload');
                    await postOrderAmendsSave(submitUrl, altPayload);
                    resultsByLineKey.set(row.lineKey, true);
                } catch (err) {
                    console.error(err);
                    resultsByLineKey.set(row.lineKey, false);
                }
            }
        }

        const allSucceeded =
            resultsByLineKey.size > 0 && Array.from(resultsByLineKey.values()).every(Boolean);

        if (allSucceeded) {
            orderAmendsPendingChanges.clear();
            cancellationFlowByLine.clear();
            orderAmendsEditModeActive = false;
            setOrderAmendsEditMode(false);
        }

        return { allSucceeded, resultsByLineKey };
    }

    async function handleTransactionSummaryDrawerAction(action) {
        switch (action) {
            case 'back':
                closeTransactionSummaryDrawer();
                break;
            case 'confirm':
                showTransactionSummaryLoadingPanel();
                try {
                    const { allSucceeded, resultsByLineKey } = await commitOrderAmendsSave();
                    if (allSucceeded) {
                        refetchOrderAfterCancel();
                        showTransactionSummarySuccessPanel();
                    } else {
                        showTransactionSummaryPartialFailurePanel(resultsByLineKey);
                    }
                } catch (err) {
                    console.error(err);
                    showTransactionSummaryErrorPanel();
                }
                break;
            case 'done':
                closeTransactionSummaryDrawer();
                if (getOrderContext()) {
                    rebuildOrderAmendsRegistry(getOrderContext());
                }
                break;
            case 'close':
                closeTransactionSummaryDrawer();
                break;
            default:
                break;
        }
    }

    function handleTransactionSummaryDrawerDelegatedClick(ev) {
        if (!transactionSummaryDrawerState.active) return;
        const drawer = getOrderTrackerDrawer();
        if (!drawer?.classList.contains('order-tracker-drawer--open')) return;

        const actionBtn = ev.target.closest('[data-oa-transaction-summary-action]');
        if (!actionBtn || !drawer.contains(actionBtn)) return;
        if (actionBtn.tagName === 'A' && actionBtn.href) return;

        ev.preventDefault();
        handleTransactionSummaryDrawerAction(actionBtn.dataset.oaTransactionSummaryAction);
    }

    function syncLinePendingQuantity(lineKey, requestedQuantity) {
        const baseline = orderAmendsBaselines.get(lineKey);
        const orderedQuantity =
            baseline?.orderedQuantity ??
            Math.max(0, parseInt(orderAmendsItemRegistry.get(lineKey)?.item?.quantity, 10) || 0);
        const existing = orderAmendsPendingChanges.get(lineKey) || {};

        if (
            requestedQuantity === orderedQuantity &&
            !existing.alternativeItemNumber &&
            !existing.alternativeItemDescription
        ) {
            orderAmendsPendingChanges.delete(lineKey);
            return;
        }

        orderAmendsPendingChanges.set(lineKey, {
            ...existing,
            requestedQuantity,
            cancelEntireLine: requestedQuantity === 0,
        });
    }

    async function postOrderAmendsSave(submitUrl, body) {
        const shopifyId = getOrderContext()?.orderSummary?.shopifyOrderInternalId;
        const payload = { ...body };
        if (shopifyId != null && shopifyId !== '') {
            payload.shopifyOrderInternalId = shopifyId;
        }

        console.log('[Order amends] POST', submitUrl, payload);

        const res = await fetch(submitUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        console.log('[Order amends] response for action', payload.action, data);

        if (!res.ok) {
            throw new Error(data.errorMessage || data.message || `HTTP ${res.status}`);
        }
        return parseShopifyOrderStatusResponse(
            data,
            t().orderAmendsQtyConfirmError || 'We could not save this change.'
        );
    }

    function saveOrderAmendsChanges() {
        if (!orderAmendsEditModeActive) return;
        if (orderCancelFlowActive) return;

        let hasAnyChange = false;
        orderAmendsItemRegistry.forEach((_entry, lineKey) => {
            if (lineHasSaveChanges(getLineStagedState(lineKey))) hasAnyChange = true;
        });
        if (!hasAnyChange) {
            global.alert(t().orderAmendsSaveChangesNone || 'No changes to save.');
            return;
        }

        const payload = collectOrderAmendsSavePayload();

        if (!validateOrderAmendsSaveBeforeSubmit()) return;

        const apiKey = getApiCredentials().clientApiKey;
        const submitUrl = buildOrderDetailsUpdateUrl(apiKey);

        openTransactionSummaryDrawer({ payload, submitUrl });
    }

    function setOrderAmendsEditMode(active) {
        orderAmendsEditModeActive = active;
        const root = getRoot();
        if (!root) return;
        if (active) {
            snapshotOrderAmendsBaselines();
        }
        root.classList.toggle('order-amends--edit-state', active);
        root.classList.toggle('order-amends--read-state', !active);
        document.querySelectorAll('.shipment__item-product-actions-amend-state').forEach((el) => {
            const container = el.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            const flowActive = lineKey ? getFlowState(lineKey).isActive : false;
            setVisiblyHidden(el, !active || flowActive);
        });
        updateSaveChangesButtonState();
    }

    function updateOrderAmendsEditModeUi() {
        setOrderAmendsEditMode(orderAmendsEditModeActive);
    }

    function orderHasAmendableLines(orderData) {
        const oi = orderData?.orderDetails?.orderItems;
        if (!oi) return false;
        const preparing = oi.preparingForDespatchShipments?.length > 0;
        const outstanding = oi.outstandingBatches?.length > 0;
        return preparing || outstanding;
    }

    function updateOrderAmendsActivateCta(orderData) {
        const show = orderHasAmendableLines(orderData);
        const activateBtn = document.getElementById('order-amends-activate-edit-mode');
        if (activateBtn) activateBtn.classList.toggle('hidden', !show);
        document.querySelectorAll('#order-amends-cancel-order, #order-amends-cancel-order-inline').forEach((cancelBtn) => {
            cancelBtn.classList.toggle('hidden', !show);
        });
    }

    function rebuildOrderAmendsRegistry(orderData) {
        orderAmendsItemRegistry.clear();
        cancellationFlowByLine.clear();
        alternativesProductsCache.clear();
        const oi = orderData?.orderDetails?.orderItems;
        if (!oi) return;

        const buckets = [
            { bucket: 'preparing', status: global.__ORDER_STATUS_PREPARING, rows: oi.preparingForDespatchShipments || [] },
            { bucket: 'outstanding', status: global.__ORDER_STATUS_OUTSTANDING, rows: oi.outstandingBatches || [] },
        ];

        buckets.forEach(({ bucket, status, rows }) => {
            rows.forEach((shipmentOrBatch, batchIndex) => {
                (shipmentOrBatch.items || []).forEach((item, itemIndex) => {
                    const lineKey = `${bucket}:${batchIndex}:${itemIndex}`;
                    orderAmendsItemRegistry.set(lineKey, {
                        item,
                        status,
                        bucket,
                        batchIndex,
                        itemIndex,
                        shipmentOrBatch,
                    });
                });
            });
        });
    }

    function resetOrderAmendsState() {
        getLineKeysInDomOrder().forEach((lineKey) => {
            if (orderAmendsPendingChanges.get(lineKey)?.alternativeItemNumber) {
                clearAlternativeSelection(lineKey);
            } else {
                exitTriggeredState(lineKey);
            }
        });

        orderAmendsEditModeActive = false;
        orderAmendsPendingChanges.clear();
        orderAmendsBaselines.clear();
        orderAmendsItemRegistry.clear();
        cancellationFlowByLine.clear();
        alternativesProductsCache.clear();
        if (orderCancelFlowActive) {
            getOrderTrackerDrawer()?.close();
        }
        if (transactionSummaryDrawerState.active) {
            resetTransactionSummaryDrawerState();
        }
        resetOrderCancelDrawerState();
        clearAllLineValidationErrors();
        setOrderAmendsEditMode(false);
    }

    function handleDelegatedClick(ev) {
        const chooseProductBtn = ev.target.closest('[data-oa-choose-product]');
        if (chooseProductBtn) {
            ev.preventDefault();
            const container = chooseProductBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) openAlternativesDrawer(lineKey);
            return;
        }

        const removeBtn = ev.target.closest('.order-amends__remove-product-btn');
        if (removeBtn) {
            ev.preventDefault();
            const lineKey = removeBtn.dataset.oaLineKey;
            if (lineKey) startRemovalFlow(lineKey, 0);
            return;
        }

        const discardBtn = ev.target.closest('[data-oa-discard-btn]');
        if (discardBtn) {
            ev.preventDefault();
            const container = discardBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) exitTriggeredState(lineKey);
            return;
        }

        const qtyDiscardBtn = ev.target.closest('[data-oa-qty-discard-btn]');
        if (qtyDiscardBtn) {
            ev.preventDefault();
            const container = qtyDiscardBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) exitQtyReductionState(lineKey);
            return;
        }

        const qtyBackBtn = ev.target.closest('[data-oa-qty-back-btn]');
        if (qtyBackBtn) {
            ev.preventDefault();
            const container = qtyBackBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (!lineKey) return;
            const flow = getFlowState(lineKey);
            flow.pendingReason = null;

            setVisiblyHidden(container.querySelector('[data-oa-qty-back-btn]'), true);
            setVisiblyHidden(container.querySelector('[data-oa-qty-discard-btn]'), false);

            setVisiblyHidden(container.querySelector('[data-oa-qty-stepper-block]'), false);
            setVisiblyHidden(container.querySelector('[data-oa-qty-next-row]'), true);

            const qtyReasonSelect = container.querySelector('[data-oa-qty-reason-select]');
            if (qtyReasonSelect) qtyReasonSelect.value = '';
            syncReasonConfirmUi(container, false);
            return;
        }

        const qtyMinus = ev.target.closest('.order-amends__qty-btn[data-oa-qty-step="-1"]');
        if (qtyMinus) {
            const wrap = qtyMinus.closest('[data-oa-qty]');
            const lineKey = wrap?.dataset.oaLineKey;
            if (!lineKey) return;
            ev.preventDefault();
            const current = parseInt(wrap.querySelector('[data-oa-qty-current]')?.textContent, 10) || 0;
            const newQty = Math.max(0, current - 1);
            const flow = getFlowState(lineKey);
            if (newQty === 0) {
                if (flow.isActive && flow.targetQuantity > 0) {
                    exitQtyReductionState(lineKey);
                }
                startRemovalFlow(lineKey, 0);
            } else if (flow.isActive && typeof flow.targetQuantity === 'number' && flow.targetQuantity > 0) {
                flow.targetQuantity = newQty;
                wrap.querySelector('[data-oa-qty-current]').textContent = String(newQty);
                updateQtyStepperLabel(lineKey);
                updateQtyMinusIcon(lineKey, newQty);
                updateLineDisplay(lineKey);
            } else {
                enterQtyReductionState(lineKey, newQty);
            }
            return;
        }

        const qtyPlus = ev.target.closest('.order-amends__qty-btn[data-oa-qty-step="1"]');
        if (qtyPlus) {
            const wrap = qtyPlus.closest('[data-oa-qty]');
            if (!wrap) return;
            const lineKey = wrap.dataset.oaLineKey;
            const flow = getFlowState(lineKey);
            const current = parseInt(wrap.querySelector('[data-oa-qty-current]')?.textContent, 10) || 0;
            const max = parseInt(wrap.dataset.oaQtyMax, 10) || 0;
            ev.preventDefault();
            if (current < max) {
                const newQty = current + 1;
                wrap.querySelector('[data-oa-qty-current]').textContent = String(newQty);
                if (flow.isActive && typeof flow.targetQuantity === 'number' && flow.targetQuantity > 0) {
                    flow.targetQuantity = newQty;
                    if (newQty >= max) {
                        exitQtyReductionState(lineKey);
                    } else {
                        updateQtyStepperLabel(lineKey);
                        updateQtyMinusIcon(lineKey, newQty);
                        updateLineDisplay(lineKey);
                        qtyPlus.disabled = newQty >= max;
                    }
                } else {
                    syncLinePendingQuantity(lineKey, newQty);
                    updateLineDisplay(lineKey);
                    qtyPlus.disabled = newQty >= max;
                }
            }
            return;
        }

        const retentionBtn = ev.target.closest('[data-oa-retention-action]');
        if (retentionBtn) {
            if (retentionBtn.tagName === 'A' && retentionBtn.href) return;
            ev.preventDefault();
            const container = retentionBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) handleRetentionAction(lineKey, retentionBtn.dataset.oaRetentionAction);
            return;
        }

        const reasonConfirmBtn = ev.target.closest('[data-oa-reason-confirm]');
        if (reasonConfirmBtn) {
            ev.preventDefault();
            const container = reasonConfirmBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) handleReasonConfirm(lineKey);
            return;
        }

        const reasonNextBtn = ev.target.closest('[data-oa-reason-next]');
        if (reasonNextBtn) {
            ev.preventDefault();
            const container = reasonNextBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) handleReasonNext(lineKey);
            return;
        }

        const reasonDiscardBtn = ev.target.closest('[data-oa-reason-discard]');
        if (reasonDiscardBtn) {
            ev.preventDefault();
            const container = reasonDiscardBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) exitTriggeredState(lineKey);
            return;
        }

        const removeUndoBtn = ev.target.closest('[data-oa-remove-undo]');
        if (removeUndoBtn) {
            ev.preventDefault();
            const container = removeUndoBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) {
                if (orderAmendsPendingChanges.get(lineKey)?.alternativeItemNumber) {
                    clearAlternativeSelection(lineKey);
                } else {
                    exitTriggeredState(lineKey);
                }
            }
            return;
        }

        const validationUndoBtn = ev.target.closest('[data-oa-validation-undo]');
        if (validationUndoBtn) {
            ev.preventDefault();
            const container = validationUndoBtn.closest('[data-oa-line-key]');
            const lineKey = container?.dataset.oaLineKey;
            if (lineKey) {
                clearLineValidationError(lineKey);
                exitTriggeredState(lineKey);
            }
        }
    }

    async function handleReasonConfirm(lineKey) {
        const container = getLineContainer(lineKey);
        const flow = getFlowState(lineKey);
        const isQtyReduction = flow.isActive && typeof flow.targetQuantity === 'number' && flow.targetQuantity > 0;
        const selectAttr = isQtyReduction ? '[data-oa-qty-reason-select]' : '[data-oa-reason-select]';
        const reasonId = flow.pendingReason || container?.querySelector(selectAttr)?.value;
        if (!reasonId) return;
        flow.selectedReason = reasonId;
        clearLineValidationError(lineKey);

        if (REASON_BRANCH[reasonId] === 'deliveryDate') {
            await routeCancellationAfterReason(createLineCancellationCtx(lineKey), reasonId);
            return;
        }

        if (!stageLineRemovePayload(lineKey, reasonId)) return;
        showRemovalSummaryStep(lineKey);
    }

    function handleReasonNext(lineKey) {
        handleReasonConfirm(lineKey);
    }

    function handleReasonChange(ev) {
        const select = ev.target.closest('[data-oa-reason-select]') || ev.target.closest('[data-oa-qty-reason-select]');
        if (!select) return;
        const container = select.closest('[data-oa-line-key]');
        const lineKey = container?.dataset.oaLineKey;
        if (!lineKey) return;

        const flow = getFlowState(lineKey);
        flow.pendingReason = select.value || null;

        const hasReason = Boolean(select.value);

        // Qty-reduction sub-flow: swap Discard↔Back and Stepper↔Next when reason changes
        const isQtyReduction = flow.isActive && typeof flow.targetQuantity === 'number' && flow.targetQuantity > 0;
        if (isQtyReduction) {
            setVisiblyHidden(container.querySelector('[data-oa-qty-discard-btn]'), hasReason);
            setVisiblyHidden(container.querySelector('[data-oa-qty-back-btn]'), !hasReason);
            setVisiblyHidden(container.querySelector('[data-oa-qty-stepper-block]'), hasReason);
            setVisiblyHidden(container.querySelector('[data-oa-qty-next-row]'), !hasReason);
        }

        syncReasonConfirmUi(container, hasReason);
        if (hasReason) clearLineValidationError(lineKey);
    }

    function initOrderAmends() {
        if (orderAmendsHandlersBound) return;
        orderAmendsHandlersBound = true;

        document.getElementById('order-amends-activate-edit-mode')?.addEventListener('click', () => {
            setOrderAmendsEditMode(true);
        });

        document.getElementById('order-amends-cancel-changes')?.addEventListener('click', () => {
            const tr = t();
            if (orderAmendsPendingChanges.size > 0) {
                const ok = global.confirm(tr.orderAmendsDiscardChangesConfirm || 'Discard all unsaved changes?');
                if (!ok) return;
            }
            resetOrderAmendsState();
            if (getOrderContext()) rebuildOrderAmendsRegistry(getOrderContext());
        });

        document.getElementById('order-amends-save-changes')?.addEventListener('click', saveOrderAmendsChanges);

        document.querySelectorAll('#order-amends-cancel-order, #order-amends-cancel-order-inline').forEach((btn) => {
            btn.addEventListener('click', startOrderCancelFlow);
        });

        document.addEventListener('click', handleOrderCancelDrawerDelegatedClick);
        document.addEventListener('click', handleTransactionSummaryDrawerDelegatedClick);
        document.addEventListener('click', handleAlternativesDrawerDelegatedClick);
        document.addEventListener('change', handleOrderCancelDrawerRadioChange);

        getOrderTrackerDrawer()?.addEventListener('order-tracker-drawer:closed', () => {
            const wasPartialFailure =
                transactionSummaryDrawerState.active && transactionSummaryDrawerState.step === 'partialFailure';

            if (orderCancelFlowActive) resetOrderCancelDrawerState();
            if (transactionSummaryDrawerState.active) resetTransactionSummaryDrawerState();
            if (alternativesDrawerState.active) resetAlternativesDrawerState();

            if (wasPartialFailure) {
                orderAmendsPendingChanges.clear();
                cancellationFlowByLine.clear();
                orderAmendsEditModeActive = false;
                setOrderAmendsEditMode(false);
                refetchOrderAfterCancel();
            }
        });

        const root = getRoot();
        root?.addEventListener('click', handleDelegatedClick);
        root?.addEventListener('change', handleReasonChange);
    }

    global.OrderAmendsFlow = {
        initOrderAmends,
        resetOrderAmendsState,
        rebuildOrderAmendsRegistry,
        updateOrderAmendsEditModeUi,
        updateOrderAmendsActivateCta,
        isOrderCancelFlowActive: () => orderCancelFlowActive,
        setCachedOrderData: (data) => {
            global.__orderTrackerCachedOrderData = data;
        },
        setFormatMoney: (fn) => {
            global.__orderTrackerFormatMoney = fn;
        },
        setStatusConstants: (preparing, outstanding) => {
            global.__ORDER_STATUS_PREPARING = preparing;
            global.__ORDER_STATUS_OUTSTANDING = outstanding;
        },
        setRefetchOrderHandler: (fn) => {
            global.__orderTrackerRefetchOrder = fn;
        },
    };
})(window);
